<template>
  <div class="form-row">
    <div class="col-12 mb-4">
      <h4 class="text-center">History Coverage (Past 5 Years)</h4>
      <history-pie-chart :chart-data="pieChartData" />
    </div>
    <form :class="{ 'loading-opacity': preloader, 'col-sm-12 p-4': isAdmin, 'col-md-12 mb-0': !isAdmin }">
      <div class="form-group">
        <h3 class="text-center mb-0 mt-3">
          Previous History Information
        </h3>
        <span>Applicants must provide a complete employment history for the past 5 years (or 10 years if specifically required for the role). Inaccurate or incomplete information may affect your application or employment status.</span>
        <div v-if="restrictValidation && historyGaps.length > 0" class="gap-warning mt-4">
          <h4 class="text-danger">⚠ History Gaps Found</h4>
          <ul class="text-danger">
            <li v-for="(gap, index) in historyGaps" :key="index">
              Missing period: {{ dateFormatter(gap.start) }} to {{ dateFormatter(gap.end) }} ({{gap.duration}})
            </li>
          </ul>
        </div>
      </div>
      <template v-if="sortedCandidateHistory.length > 0">
          <div class="d-flex flex-wrap justify-content-between">
            <div v-for="(item, index) in sortedCandidateHistory" :key="index"  :class="isReadOnly ? 'col-12 my-2' : 'col-md-6 my-2 d-flex'">
              <div class="card-item w-100">
                <label v-if="item.current_status" class="col-sm-12 mb-sm-1 current-address">Currently Here</label>
                <div class="form-group row align-items-center">
                  <div class="col-6">
                    <template v-if="item.type === 0">
                      <label class="col-sm-12 mb-sm-1">{{ item.title }} | Employment</label>
                      <label class="col-sm-12 mb-sm-1">{{ item.company_name }}</label>
                      <label class="col-sm-12 mb-sm-1">{{ dateFormatter(item.start_date) }} -
                        {{ formatEndDate(item) }}</label>
                      <label class="col-sm-12 mb-sm-1">{{ calculateDifference(item.end_date, item.start_date) }}</label>
                    </template>
                    <template v-if="item.type === 1">
                      <label class="col-sm-12 mb-sm-1">{{ item.qualification }} | Education</label>
                      <label class="col-sm-12 mb-sm-1">{{ item.institute }}</label>
                      <label class="col-sm-12 mb-sm-1">{{ dateFormatter(item.start_date) }} -
                        {{ formatEndDate(item) }} </label>
                      <label class="col-sm-12 mb-sm-1">{{ calculateDifference(item.end_date, item.start_date) }}</label>
                    </template>
                    <template v-if="item.type === 2">
                      <label class="col-sm-12 mb-sm-1">{{ item.name }} | Self-Employment / Trade</label>
                      <label class="col-sm-12 mb-sm-1">{{ item.utr_number }}</label>
                      <label class="col-sm-12 mb-sm-1">{{ dateFormatter(item.start_date) }} - {{ formatEndDate(item) }}</label>
                      <label class="col-sm-12 mb-sm-1">{{ calculateDifference(item.end_date, item.start_date) }}</label>
                    </template>
                    <template v-if="item.type === 3">
                      <label class="col-sm-12 mb-sm-1">{{ item.is_other ? 'Other' : 'Unemployment' }}</label>
                      <label class="col-sm-12 mb-sm-1">
                        {{ dateFormatter(item.start_date) }} / {{ formatEndDate(item) }}
                      </label>
                      <label class="col-sm-12 mb-sm-1">{{ calculateDifference(item.end_date, item.start_date) }}</label>
                      <label class="col-sm-12 mb-sm-1">{{ item.more_information && item.more_information}}</label>
                    </template>
                  </div>


                  <div class="col-6" v-if="isAdmin">
                    <div v-if="item.screening_endorsement && item.screening_endorsement.requested_at">
                      Endorsements
                      <p><strong>Requested At:</strong> {{ item.screening_endorsement.requested_at.split('T')[0] }}</p>
                      <p v-if="item.screening_endorsement.endorsed_at">
                        <strong>Endorsed At:</strong> {{ item.screening_endorsement.endorsed_at }}
                      </p>
                      <p>
                        <strong>Status:</strong>
                        <a
                            :href="statusLinks[item.screening_endorsement.id] || '#'"
                            target="_blank"
                            rel="noopener noreferrer"
                            @click.prevent="fetchStatus(item.screening_endorsement.id)"
                        >
                          {{ item.screening_endorsement.status || 'Loading...' }}
                        </a>
                      </p>
                    </div>
                    <div v-if="getLatestStatus(item)"
                        :class="getHighlightClass(getLatestStatus(item))"
                        :style="getHighlightStyle(getLatestStatus(item))"
                    >
                      {{ getStatusValue(getLatestStatus(item)) }}
                    </div>

                    <div v-if="!getLatestStatus(item)"
                         class="highlight-orange"
                    >Verification Pending
                    </div>

                  </div>

                  <div class="col-6" v-if="isReadOnly">
                    <template v-if="item.type === 0">
                      <div class="form-group mb-2 col-md-12 d-flex justify-content-between small">
                        <strong>Previous Employment Details:</strong>
                        <span>{{ item.is_have_p45_or_p46 }}</span>
                      </div>

                      <div class="form-group mb-2 col-md-12 d-flex justify-content-between small" v-if="item.is_have_p45_or_p46 === 'p45'">
                        <strong>Attached P45:</strong>
                        <span>{{ item.copy_of_p45.name || 'No file uploaded' }}</span>
                      </div>
                      <div v-if="item.is_have_p45_or_p46 === 'p45' && item.copy_of_p45" class="col-sm-12 document-preview-container mt-3">
                        <div class="document-preview-card" @click="viewCertificate(item.copy_of_p45)" style="cursor: pointer;">
                          <div class="file-thumbnail-box">
                            <button class="remove-preview-btn" @click.stop="removeEmploymentP45ForItem(item)">
                              <app-icon name="x" style="width: 14px; height: 14px;" />
                            </button>
                            <img v-if="isImage(item.copy_of_p45)" :src="getFileUrl(item.copy_of_p45)" alt="P45 Preview">
                            <div v-else class="file-icon-large">
                              <app-icon :name="getFileIcon(item.copy_of_p45)" style="width: 48px; height: 48px;" />
                            </div>
                          </div>
                          <div class="file-name-text">
                            {{ (item.copy_of_p45 || '').toString().split('/').pop() }}
                          </div>
                          <div class="upload-status-badge">
                            <app-icon name="check-circle" style="width: 14px; height: 14px;" /> Uploaded <app-icon name="check" style="width: 14px; height: 14px;" />
                          </div>
                        </div>
                      </div>

                      <div class="form-group mb-2 col-md-12 d-flex justify-content-between small" v-if="item.is_have_p45_or_p46 === 'p46'">
                        <strong>P46 Detail:</strong>
                        <span>{{ item.p46_detail }}</span>
                      </div>

                      <div class="form-group mb-2 col-md-12 d-flex justify-content-between small" v-for="(value, label) in {
                            'Job Title': item.title,
                            'Company Name': item.company_name,
                            'Address': item.company_address,
                            'Reason for Leaving': item.reason_for_leaving,
                            'Contact Name': item.contact_name,
                            'Contact Number': item.contact_number,
                            'Employer Email': item.contact_email,
                            'Employment Start Date': item.start_date,
                            'Employment End Date': item.end_date
                          }" :key="label">
                        <strong>{{ label }}:</strong>
                        <span>{{ value || 'N/A' }}</span>
                      </div>
                    </template>

                    <template v-if="item.type === 1">
                      <div class="form-group mb-2 col-md-12 d-flex justify-content-between small" v-for="(value, label) in {
                          'Qualification': item.qualification,
                          'Institute': item.institute,
                          'Award Date': item.award_date,
                          'Course Length': item.course_length,
                          'Contact Name': item.contact_name,
                          'Contact Number': item.contact_number,
                          'Contact Email': item.contact_email,
                          'Institute Address': item.institute_address,
                          'From': item.start_date,
                          'To': item.end_date
                        }" :key="label">
                        <strong>{{ label }}:</strong>
                        <span>{{ value || 'N/A' }}</span>
                      </div>
                    </template>

                    <template v-if="item.type === 2">
                      <div class="form-group mb-2 col-md-12 d-flex justify-content-between small" v-for="(value, label) in {
                          'Trading Name / Business Name': item.name,
                          'UTR / Company Number': item.utr_number,
                          'Trading / Business Details': item.details,
                          'Trading / Business Address': item.address,
                          'Accountant Name': item.accountant_name,
                          'Accounting Firm': item.accounting_firm,
                          'Contact Number': item.contact_number,
                          'Contact Email': item.contact_email,
                          'From': item.start_date,
                          'To': item.end_date
                      }" :key="label">
                        <strong>{{ label }}:</strong>
                        <span>{{ value || 'N/A' }}</span>
                      </div>
                    </template>

                    <template v-if="item.type === 3">
                      <div class="form-group mb-2 col-md-12">
                        <div class="d-flex justify-content-between small mb-1">
                          <strong>{{ item.is_other ? 'Other' : 'Unemployment' }} Start Date:</strong>
                          <span>{{ item.start_date || 'N/A' }}</span>
                        </div>
                        <div class="d-flex justify-content-between small mb-1">
                          <strong>{{ item.is_other ? 'Other' : 'Unemployment' }} End Date:</strong>
                          <span>{{ item.end_date || 'N/A' }}</span>
                        </div>
                        <div v-if="item.files && item.files.length" class="col-sm-12 document-preview-container mt-2">
                          <div
                            v-for="file in item.files"
                            :key="(file.id || file.name || file.file_name)"
                            class="document-preview-card"
                            @click="viewCertificate((file.path || file.file_path))"
                            style="cursor: pointer;"
                          >
                            <div class="file-thumbnail-box">
                              <img v-if="isImage((file.name || file.file_name || file.path || file.file_path))"
                                   :src="getFileUrl((file.path || file.file_path))"
                                   alt="Document Preview">
                              <div v-else class="file-icon-large">
                                <app-icon :name="getFileIcon((file.name || file.file_name || file.path || file.file_path))" style="width: 48px; height: 48px;" />
                              </div>
                            </div>
                            <div class="file-name-text">
                              {{ (file.file_name || file.name || '').toString() }}
                            </div>
                            <div class="upload-status-badge">
                              <app-icon name="check-circle" style="width: 14px; height: 14px;" /> Uploaded <app-icon name="check" style="width: 14px; height: 14px;" />
                            </div>
                          </div>
                        </div>
                        <div v-else>
                          <strong>Uploaded Document:</strong>
                          <span class="ml-2">N/A</span>
                        </div>
                      </div>
                    </template>
                  </div>

                  <div class="col-6">
                    <div
                        v-if="!isAdmin && isRevertToApplicant(getLatestStatus(item))"
                        class="highlight-orange"
                    >To do: {{  getStatusComment(getLatestStatus(item)) }}
                    </div>
                  </div>


                  <div class="col-sm-12 mt-sm-3" v-if="!isReadOnly">
                    <div class="row">
                      <div class="col-12 d-flex flex-wrap align-items-center action-buttons">
                        <el-button class="btn btn-danger mr-3 btn-update"
                                   @click="updateBtnHandler(item.id, item.type)">
                          Edit
                        </el-button>
                        <el-button class="btn btn-danger mr-3"
                                   @click="removeBtnHandler(item.id, item.type)">
                          Remove
                        </el-button>

                        <div class="mr-3" v-if="isAdmin">
                          <drawer-component title="Action History" size="70%" btnText="Take Action"
                                            v-if="hasScreeningSchedule" :open="true"
                          >
                            <history-comments
                                :comment="item.screening_status"
                                :applicantId="candidateId"
                                :statusable_type="item.statusable_type"
                                :statusable_id="item.id"
                                :candidate="item"
                                :applicant="applicant"
                                :company="company"
                                :dateFormatter="dateFormatter"
                                :emailAddress="emailAddress"
                                @status-updated="handleStatusUpdate"
                                @status-deleted="handleStatusDelete"
                            />
                          </drawer-component>
                          <div v-else class="screening-note">
                            Add this profile to Screening to proceed.
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
      </template>
      <slot v-if="!isReadOnly">
  <div class="form-group row mt-sm-5 text-center">
  <app-input
    class="mx-auto mb-sm-4"
    type="radio"
    :list="employmentTypeList"
    v-model="employment_type"
  />
</div>

<!--        Employment Form-->
        <template v-if="employment_type === historyType.Employment">
          <div class="row">
            <div class="form-group mb-3 col-md-12 align-items-center">
              <label class="col-sm-12 mb-sm-2">
                Do you have p45 or p46? Click others if not applicable.
                <span class="text-danger">*</span>
              </label>
              <app-input class="col-sm-12" type="radio" :list="employmentDetailsTypeList" v-model="employment.is_have_p45_or_p46"  />
            </div>
            <template v-if="employment.is_have_p45_or_p46 === 'p45'">
            <div class="form-group mb-3 mt-3 col-md-6 align-items-center">
                 <label class="col-sm-12 mb-sm-0">Attach a copy of P45 <span class="text-danger">*</span></label>
                <app-input class="col-sm-8 mr-sm-2 mb-sm-2" type="file" @input="documentp45Change" />
                <div v-if="employment.p45_pending || employment.copy_of_p45 || employment.p45_preview" class="col-sm-12 document-preview-container mt-4">
                  <div class="document-preview-card" @click="viewCertificate(employment.copy_of_p45 || employment.p45_preview)" style="cursor: pointer;">
                    <div class="file-thumbnail-box">
                      <button class="remove-preview-btn" @click.stop="removeEmploymentP45">
                        <app-icon name="x" style="width: 14px; height: 14px;" />
                      </button>
                      <template v-if="employment.p45_pending">
                        <img v-if="isImage(employment.p45_name || employment.p45_preview)" :src="employment.p45_preview" alt="P45 Preview">
                        <div v-else class="file-icon-large">
                          <app-icon :name="getFileIcon(employment.p45_name)" style="width: 48px; height: 48px;" />
                        </div>
                      </template>
                      <template v-else-if="employment.copy_of_p45">
                        <img v-if="isImage(employment.p45_name || employment.copy_of_p45)" :src="getFileUrl(employment.copy_of_p45)" alt="P45 Preview">
                        <div v-else class="file-icon-large">
                          <app-icon :name="getFileIcon(employment.p45_name || employment.copy_of_p45)" style="width: 48px; height: 48px;" />
                        </div>
                      </template>
                    </div>
                    <div class="file-name-text" :title="getFileDisplayName(employment.p45_name || employment.copy_of_p45)">
                      {{ getFileDisplayName(employment.p45_name || employment.copy_of_p45) || 'P45' }}
                    </div>
                    <div class="upload-status-badge">
                      <template v-if="!employment.p45_pending && employment.copy_of_p45">
                        <app-icon name="check-circle" style="width: 14px; height: 14px;" /> Uploaded <app-icon name="check" style="width: 14px; height: 14px;" />
                      </template>
                      <template v-else-if="employment.p45_pending">
                        <app-icon name="check-circle" style="width: 14px; height: 14px;" /> Selected <app-icon name="check" style="width: 14px; height: 14px;" />
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <template v-if="employment.is_have_p45_or_p46 === 'p46'">
              <div class="form-group mb-3 mt-3 col-md-6 align-items-center">
                <app-input class="col-sm-12 mb-sm-4" type="radio" :list="p46List" v-model="employment.p46_detail" :required="true" />
              </div>
            </template>
            <div class="form-group mb-3 mt-3 col-md-6 align-items-center">
              <label class="col-sm-12 mb-sm-0">Job Title <span class="text-danger">*</span></label>
              <app-input class="col-sm-8 mr-sm-2 mb-sm-2" type="text" v-model="employment.title" :required="true" />
            </div>
            <div class="form-group mb-3 mt-3 col-md-6 align-items-center">
              <label class="col-sm-12 mb-sm-0">Company Name <span class="text-danger">*</span></label>
              <app-input class="col-sm-8 mr-sm-2 mb-sm-2" type="text" v-model="employment.company_name" :required="true" />
            </div>
            <div class="form-group mb-3 mt-3 col-md-6 align-items-center">
              <label class="col-sm-12 mb-sm-0">Address <span class="text-danger">*</span></label>
              <app-input class="col-sm-8 mr-sm-2 mb-sm-2" type="text" v-model="employment.company_address" :required="true" />
            </div>
            <div class="form-group mb-3 mt-3 col-md-6 align-items-center">
              <label class="col-sm-12 mb-sm-0">Reason for Leaving <span class="text-danger">*</span></label>
              <app-input class="col-sm-8 mr-sm-2 mb-sm-2" type="text" v-model="employment.reason_for_leaving" :required="true" />
            </div>
            <div class="form-group mb-3 mt-3 col-md-6 align-items-center">
              <label class="col-sm-12 mb-sm-0">Contact Name <span class="text-danger">*</span></label>
              <app-input class="col-sm-8 mr-sm-2 mb-sm-2" type="text" v-model="employment.contact_name" :required="true" />
            </div>
            <div class="form-group mb-3 mt-3 col-md-6 align-items-center">
              <label class="col-sm-12 mb-sm-0">Contact Number <span class="text-danger">*</span></label>
              <app-input class="col-sm-8 mr-sm-2 mb-sm-2" type="tel-input-custom" v-model="employment.contact_number" :required="true" />
            </div>
            <div class="form-group mb-3 mt-3 col-md-6 align-items-center">
              <label class="col-sm-12 mb-sm-0">Contact Email</label>
              <app-input class="col-sm-8 mr-sm-2 mb-sm-2" type="email" v-model="employment.contact_email" />
            </div>
            <div class="form-group mb-3 mt-3 col-md-6 align-items-center">
              <label class="col-sm-12 mb-sm-0">Employment Start Date <span class="text-danger">*</span></label>
              <app-input class="col-sm-8 mr-sm-2 mb-sm-2" type="date" v-model="employment.start_date" :max-date="employment.current_status ? new Date() : null" :required="true" />
              <div class="error col-sm-12" v-if="!$v.employment.start_date.isValidFromDate">
                From Date Can't Be Greater Than To Date.
              </div>
            </div>

              <div class="form-group mb-3 mt-3 col-md-6 align-items-center">
                <label class="col-sm-12 mb-sm-0">Employment End Date</label>
                <div class="d-flex align-items-center w-100">
                  <app-input
                      class="col-sm-8 mr-sm-2 mb-sm-2"
                      type="date"
                      v-model="employment.end_date"
                      :disabled="employment.current_status"
                      @input="refreshValidation('employment')"
                  />
                    <div class="d-flex align-items-center ml-2">
                      <label class="to-present-label mb-1 mr-2">To Present</label>
                    <el-checkbox
                        v-model="employment.current_status"
                        @change="checked => checked ? handleCurrentStatusChange('employment') : handleUncheckCurrentStatus('employment')"
                    />
                  </div>
                </div>
              </div>
<!--            <div class="form-group row align-items-center mt-2">-->
<!--              <label class="col-sm-8">Currently Working Here?</label>-->
<!--              <el-checkbox v-model="employment.current_status" @change="() => handleCurrentStatusChange('employment')" />-->
<!--            </div>-->
          </div>
        </template>
<!--Education Form-->
        <template v-if="employment_type === historyType.Education">
          <div class="form-group row align-items-center ml-sm-2">
            <label class="col-sm-12 mb-sm-0">Qualification <span class="text-danger">*</span></label>
            <app-input class="col-sm-8 mr-sm-2 mb-sm-2" type="text" v-model="education.qualification" :required="true" />
            <label class="col-sm-12 mb-sm-0">Institute <span class="text-danger">*</span></label>
            <app-input class="col-sm-8 mr-sm-2 mb-sm-2" type="text" v-model="education.institute" :required="true" />
            <label class="col-sm-12 mb-sm-0">Award Date <span class="text-danger">*</span></label>
            <app-input class="col-sm-8 mr-sm-2 mb-sm-2" type="date" v-model="education.award_date" :required="true" />
            <label class="col-sm-12 mb-sm-0">Course Length <span class="text-danger">*</span></label>
            <app-input class="col-sm-8 mr-sm-2 mb-sm-2" type="text" v-model="education.course_length" :required="true" />
            <label class="col-sm-12 mb-sm-0">Contact Name <span class="text-danger">*</span></label>
            <app-input class="col-sm-8 mr-sm-2 mb-sm-2" type="text" v-model="education.contact_name" :required="true" />
            <label class="col-sm-12 mb-sm-0">Contact Number <span class="text-danger">*</span></label>
            <app-input class="col-sm-8 mr-sm-2 mb-sm-2" type="tel-input-custom" v-model="education.contact_number" :required="true" />
            <label class="col-sm-12 mb-sm-0">Contact Email</label>
            <app-input class="col-sm-8 mr-sm-2 mb-sm-2" type="email" v-model="education.contact_email" />
            <label class="col-sm-12 mb-sm-0">Institute Address <span class="text-danger">*</span></label>
            <app-input class="col-sm-8 mr-sm-2 mb-sm-2" type="textarea" v-model="education.institute_address" :required="true" />
            <label class="col-sm-12 mb-sm-0">From <span class="text-danger">*</span></label>
            <app-input class="col-sm-8 mr-sm-2 mb-sm-2" type="date" v-model="education.start_date" :max-date="education.current_status ? new Date() : null" :required="true" />
            <div class="error col-sm-12" v-if="!$v.education.start_date.isValidFromDate">
              From Date Can't Be Greater Than To Date.
            </div>
            <label class="col-sm-12 mb-sm-0">To</label>
            <div class="d-flex align-items-center w-100">
              <app-input
                  class="col-sm-8 mr-sm-2 mb-sm-2"
                  type="date"
                  v-model="education.end_date"
                  :disabled="education.current_status"
                  @input="refreshValidation('education')"
              />
              <div class="d-flex align-items-center ml-2">
                <label class="mb-0 mr-2 to-present-label mb-1">To Present</label>
                <el-checkbox v-model="education.current_status" @change="checked => checked ? handleCurrentStatusChange('education') : handleUncheckCurrentStatus('education')" />
              </div>
            </div>
          </div>
        </template>
<!--Self Employment Form-->
        <template v-if="employment_type === historyType.SelfEmploymentTrade">
          <div class="form-group row align-items-center ml-sm-2">
            <label class="col-sm-12 mb-sm-0">Trading Name / Business Name <span class="text-danger">*</span></label>
            <app-input class="col-sm-8 mr-sm-2 mb-sm-2" type="text" v-model="selfEmployment.name" :required="true" />
            <label class="col-sm-12 mb-sm-0">UTR / Company Number <span class="text-danger">*</span></label>
            <app-input class="col-sm-8 mr-sm-2 mb-sm-2" type="text" v-model="selfEmployment.utr_number" :required="true" />
            <label class="col-sm-12 mb-sm-0">Trading / Business Details <span class="text-danger">*</span></label>
            <app-input class="col-sm-8 mr-sm-2 mb-sm-2" type="textarea" v-model="selfEmployment.details" :required="true" />
            <label class="col-sm-12 mb-sm-0">Trading / Business Address <span class="text-danger">*</span></label>
            <app-input class="col-sm-8 mr-sm-2 mb-sm-2" type="text" v-model="selfEmployment.address" :required="true" />
            <label class="col-sm-12 mb-sm-0">Accountant Name <span class="text-danger">*</span></label>
            <app-input class="col-sm-8 mr-sm-2 mb-sm-2" type="text" v-model="selfEmployment.accountant_name" :required="true" />
            <label class="col-sm-12 mb-sm-0">Accounting Firm <span class="text-danger">*</span></label>
            <app-input class="col-sm-8 mr-sm-2 mb-sm-2" type="textarea" v-model="selfEmployment.accounting_firm" :required="true" />
            <label class="col-sm-12 mb-sm-0">Contact Number <span class="text-danger">*</span></label>
            <app-input class="col-sm-8 mr-sm-2 mb-sm-2" type="tel-input-custom" v-model="selfEmployment.contact_number" :required="true" />
            <label class="col-sm-12 mb-sm-0">Contact Email</label>
            <app-input class="col-sm-8 mr-sm-2 mb-sm-2" type="email" v-model="selfEmployment.contact_email" />
            <label class="col-sm-12 mb-sm-0">From <span class="text-danger">*</span></label>
            <app-input class="col-sm-8 mr-sm-2 mb-sm-2" type="date" v-model="selfEmployment.start_date" :max-date="selfEmployment.current_status ? new Date() : null" :required="true" />
            <div class="error col-sm-12" v-if="!$v.selfEmployment.start_date.isValidFromDate">
              From Date Can't Be Greater Than To Date.
            </div>

            <label class="col-sm-12 mb-sm-0">To</label>
            <app-input
                class="mr-sm-2 col-sm-8 mb-sm-2"
                type="date"
                v-model="selfEmployment.end_date"
                :disabled="selfEmployment.current_status"
                @input="refreshValidation('selfEmploymentTrade')"
            />
            <div class="form-group row align-items-center mt-2">
              <label class="col-sm-8">Currently Self-Employed?</label>
                <el-checkbox
                  v-model="selfEmployment.current_status"
                  @change="checked => checked ? handleCurrentStatusChange('selfEmploymentTrade') : handleUncheckCurrentStatus('selfEmploymentTrade')"
                />
            </div>
          </div>
        </template>

        <!--Un Employment Preview-->
        <template v-if="employment_type === historyType.Unemployed">
          <div class="form-group row align-items-center ml-sm-2">
            <label class="col-sm-12 mb-sm-0">Unemployment Start Date <span class="text-danger">*</span></label>
            <app-input class="col-sm-8 mr-sm-2 mb-sm-2" type="date" v-model="unEmployment.start_date" :max-date="unEmployment.current_status ? new Date() : null" />
            <div class="error col-sm-12" v-if="!$v.unEmployment.start_date.isValidFromDate">
              From Date Can't Be Greater Than To Date.
            </div>

           <label class="col-sm-12 mb-sm-0">Unemployment End Date</label>
            <app-input
                class="col-sm-8 mr-sm-2 mb-sm-2"
                type="date"
                v-model="unEmployment.end_date"
                :disabled="unEmployment.current_status"
                @input="refreshValidation('unemployed')"
            />
            <div class="form-group row align-items-center mt-2">
              <label class="col-sm-8">Currently Unemployed?</label>
                <el-checkbox
                  v-model="unEmployment.current_status"
                  @change="checked => checked ? handleCurrentStatusChange('unemployed') : handleUncheckCurrentStatus('unemployed')"
                />
            </div>
            <label class="col-sm-12 mb-sm-0">More Information</label>
            <app-input class="col-sm-8 mr-sm-2 mb-sm-2" type="textarea" v-model="unEmployment.more_information" />
            <label class="col-sm-12 mb-sm-0">Please upload proof document for Unemployment.</label>
            <upload-btn :fileObj="unEmployment.files" :renderType="type.CandidateDetail" :emailAddress="emailAddress" @selected="onCandidateDetailFileSelected('unemployed', $event)" :auto-upload="false" />
            <div v-if="selectedFile" class="col-sm-12 document-preview-container mt-4">
              <div class="document-preview-card" style="cursor: pointer;" @click="viewCertificate(selectedFile.file_path)">
                <div class="file-thumbnail-box">
                  <button class="remove-preview-btn" @click.stop="removePreviewFile">
                    <app-icon name="x" style="width: 14px; height: 14px;" />
                  </button>
                  <img v-if="isImage(selectedFile.file_name)"
                       :src="getFileUrl(selectedFile.file_path)"
                       alt="Document Preview">
                  <div v-else class="file-icon-large">
                    <app-icon :name="getFileIcon(selectedFile.file_name)" style="width: 48px; height: 48px;" />
                  </div>
                </div>
                <div class="file-name-text">
                  {{ selectedFile.file_name }}
                </div>
                <div class="upload-status-badge">
                  <template v-if="selectedFile.is_pending">
                    <app-icon name="check-circle" style="width: 14px; height: 14px;" /> Selected <app-icon name="check" style="width: 14px; height: 14px;" />
                  </template>
                  <template v-else>
                    <app-icon name="check-circle" style="width: 14px; height: 14px;" /> Uploaded <app-icon name="check" style="width: 14px; height: 14px;" />
                  </template>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!--        Other Form-->
        <template v-if="employment_type === historyType.Others">
          <history-others

              :othersObject="others"
              :emailAddress="emailAddress"
              :validator="$v.others"
              @status-change="checked => checked ? handleCurrentStatusChange('others') : handleUncheckCurrentStatus('others')"
          />
        </template>

        <div class="row mb-sm-2">
          <div class="col-sm-12">
            <el-button class="btn el-button--success"
                       :disabled="hasValidationError"
                       @click="() => addBtnHandler()">
              Save And Add Another {{ getValueById(employment_type) }}
            </el-button>

            <el-button class="btn el-button--warning ml-2"
                       v-if="employment_type"
                       @click="employment_type = null">
              Clear Selection
            </el-button>
          </div>

        </div>


      <template v-if="is_modal">
        <app-modal
            :modal-id="modalId"
            modal-alignment="top"
            modal-size="fullscreen"
            @close-modal="closeModal"
        >
          <template slot="header">
            <h5 class="modal-title">{{ $t("History Update") }}</h5>
            <button
                type="button"
                class="close outline-none"
                data-dismiss="modal"
                aria-label="Close"
            >
        <span>
          <app-icon :name="'x'"></app-icon>
        </span>
            </button>
          </template>
          <template slot="body">
            <div class="card card-with-search border-0 card-with-shadow">
              <template v-if="employment_type === historyType.Employment">
                <div class="form-group mb-3 col-md-6 align-items-center">
                  <label class="col-sm-12 mb-2">Do you have other employment? <span class="text-danger">*</span></label>
                  <app-input class="col-sm-12"
                             type="radio"
                             :list="yesOrNoList"
                             v-model="employment.is_other_employment"/>
                </div>
                <template v-if="employment.is_other_employment === 'yes'">
                  <div class="form-group mb-3 col-md-12 align-items-center">
                    <label class="col-sm-12 mb-2">
                      Would you continue other employment, if you were successful in obtaining
                      employment with us?
                    </label>
                    <app-input class="col-sm-12"
                               type="text"
                               @keyup="(event) => textOnChange(event, 'continue_other_employment', validationType.Alphabet)"
                               v-model="employment.continue_other_employment"
                               placeholder=""
                    />
                  </div>
                </template>
                <div class="form-group mb-3 col-md-6 align-items-center">
                  <label class="col-sm-12 mb-sm-2">Do you have p45 or p46? Click others if not applicable. <span class="text-danger">*</span></label>
                  <app-input class="col-sm-12"
                             type="radio"
                             :list="employmentDetailsTypeList"
                             v-model="employment.is_have_p45_or_p46"
                             placeholder=""
                  />
                </div>
                <template v-if="employment.is_have_p45_or_p46 === 'p45'">
                  <div class="form-group mb-3 col-md-6 align-items-center">
                    <label class="col-sm-12 mb-2">Attach a copy of P45</label>
                    <app-input class="col-sm-12"
                               type="file"
                               @input="documentp45Change"
                               :label="$t('upload_your_file')"
                               placeholder="attach copy"
                    />
                    <div v-if="employment.p45_pending || employment.copy_of_p45 || employment.p45_preview" class="col-sm-12 document-preview-container mt-4">
                      <div class="document-preview-card" @click="viewCertificate(employment.copy_of_p45 || employment.p45_preview)" style="cursor: pointer;">
                        <div class="file-thumbnail-box">
                          <button class="remove-preview-btn" @click.stop="removeEmploymentP45">
                            <app-icon name="x" style="width: 14px; height: 14px;" />
                          </button>
                          <template v-if="employment.p45_pending">
                            <img v-if="isImage(employment.p45_name || employment.p45_preview)" :src="employment.p45_preview" alt="P45 Preview">
                            <div v-else class="file-icon-large">
                              <app-icon :name="getFileIcon(employment.p45_name)" style="width: 48px; height: 48px;" />
                            </div>
                          </template>
                          <template v-else-if="employment.copy_of_p45">
                            <img v-if="isImage(employment.p45_name || employment.copy_of_p45)" :src="getFileUrl(employment.copy_of_p45)" alt="P45 Preview">
                            <div v-else class="file-icon-large">
                              <app-icon :name="getFileIcon(employment.p45_name || employment.copy_of_p45)" style="width: 48px; height: 48px;" />
                            </div>
                          </template>
                        </div>
                        <div class="file-name-text" :title="employment.p45_name">
                          {{ getFileDisplayName(employment.p45_name || employment.copy_of_p45) || 'P45' }}
                        </div>
                        <div class="upload-status-badge">
                          <template v-if="!employment.p45_pending && employment.copy_of_p45">
                            <app-icon name="check-circle" style="width: 14px; height: 14px;" /> Uploaded <app-icon name="check" style="width: 14px; height: 14px;" />
                          </template>
                          <template v-else-if="employment.p45_pending">
                            <app-icon name="check-circle" style="width: 14px; height: 14px;" /> Selected <app-icon name="check" style="width: 14px; height: 14px;" />
                          </template>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
                <template v-if="employment.is_have_p45_or_p46 === 'p46'">
                  <div class="form-group mb-3 mt-3 col-md-6 align-items-center">
                    <app-input class="col-sm-12 mb-sm-4"
                               type="radio"
                               :list="p46List"
                               v-model="employment.p46_detail"
                               placeholder=""
                    />
                  </div>
                </template>
                <div class="form-group row align-items-center ml-sm-2">
                  <label class="col-sm-12 mb-sm-0">Job Title</label>
                  <app-input class="col-sm-8 mr-sm-2 mb-sm-2"
                             type="text"
                             v-model="employment.title"
                             placeholder=""
                  />
                </div>
                  <div class="form-group row align-items-center ml-sm-2">
                  <label class="col-sm-12 mb-sm-0">Job Description</label>
                  <app-input class="col-sm-8 mr-sm-2 mb-sm-2"
                             type="textarea"
                             v-model="employment.description"
                             placeholder=""
                  />
                  </div>

                  <div class="form-group row align-items-center ml-sm-2">
                  <label class="col-sm-12 mb-sm-0">Company Name</label>
                  <app-input class="col-sm-8 mr-sm-2 mb-sm-2"
                             type="text"
                             v-model="employment.company_name"
                             placeholder=""
                  />
                    </div>
                  <div class="form-group row align-items-center ml-sm-2">
                  <label class="col-sm-12 mb-sm-0">Address</label>
                  <app-input class="col-sm-8 mr-sm-2 mb-sm-2"
                             type="text"
                             v-model="employment.company_address"
                             placeholder=""
                  />
                      </div>
                  <div class="form-group row align-items-center ml-sm-2">
                  <label class="col-sm-12 mb-sm-0">Reason for Leaving</label>
                  <app-input class="col-sm-8 mr-sm-2 mb-sm-2"
                             type="text"
                             v-model="employment.reason_for_leaving"
                             placeholder=""
                  />
                        </div>
                  <div class="form-group row align-items-center ml-sm-2">
                  <label class="col-sm-12 mb-sm-0">Contact Name</label>
                  <app-input class="col-sm-8 mr-sm-2 mb-sm-2"
                             type="text"
                             v-model="employment.contact_name"
                             placeholder=""
                  />
                          </div>
                  <div class="form-group row align-items-center ml-sm-2">
                  <label class="col-sm-12 mb-sm-0">Contact Number</label>
                  <app-input class="col-sm-8 mr-sm-2 mb-sm-2"
                             type="tel-input-custom"
                             v-model="employment.contact_number"
                             placeholder=""
                  />
                            </div>
                  <div class="form-group row align-items-center ml-sm-2">
                  <label class="col-sm-12 mb-sm-0">Contact Email</label>
                  <app-input class="col-sm-8 mr-sm-2 mb-sm-2"
                             type="email"
                             v-model="employment.contact_email"
                             placeholder=""
                  />
                  </div>
                  <div class="form-group row align-items-center ml-sm-2">
                  <label class="col-sm-12 mb-sm-0">Employment Start Date</label>
                  <app-input class="col-sm-8 mr-sm-2 mb-sm-2"
                             type="date"
                             v-model="employment.start_date"
                             :max-date="employment.current_status ? new Date() : null"
                             placeholder=""
                  />
                  </div>

                  <div class="form-group row align-items-center ml-sm-2">
                  <div class="error" v-if="!$v.employment.start_date.isValidFromDate">
                    From Date Can't Be Greater Than To Date.
                  </div>
                    <label class="col-sm-12 mb-sm-0">Employment End Date</label>
                    <div class="d-flex align-items-center w-100">
                      <app-input
                          class="col-sm-8 mr-sm-2 mb-sm-2"
                          type="date"
                          v-model="employment.end_date"
                          :disabled="employment.current_status"
                          @input="refreshValidation('employment')"
                      />
                      <div class="d-flex align-items-center ml-2">
                        <label class="mb-0 mr-2 small">To Present</label>
                        <el-checkbox
                            v-model="employment.current_status"
                            @change="checked => checked ? handleCurrentStatusChange('employment') : handleUncheckCurrentStatus('employment')"
                        />
                      </div>
                    </div>
                </div>
              </template>

<!--              Education Form-->
              <template v-if="employment_type === historyType.Education">
                <div class="form-group row align-items-center ml-sm-2">
                  <label class="col-sm-12 mb-sm-0">Qualification</label>
                  <app-input class="col-sm-8 mr-sm-2 mb-sm-2"
                             type="text"
                             v-model="education.qualification"
                             placeholder=""
                  />
                  <label class="col-sm-12 mb-sm-0">Institute</label>
                  <app-input class="col-sm-8 mr-sm-2 mb-sm-2"
                             type="text"
                             v-model="education.institute"
                             placeholder=""
                  />
                  <label class="col-sm-12 mb-sm-0">Award Date</label>
                  <app-input class="col-sm-8 mr-sm-2 mb-sm-2"
                             type="date"
                             v-model="education.award_date"
                             placeholder=""
                  />
                  <label class="col-sm-12 mb-sm-0">Course Length</label>
                  <app-input class="col-sm-8 mr-sm-2 mb-sm-2"
                             type="text"
                             v-model="education.course_length"
                             placeholder=""
                  />
                  <label class="col-sm-12 mb-sm-0">Contact Name</label>
                  <app-input class="col-sm-8 mr-sm-2 mb-sm-2"
                             type="text"
                             v-model="education.contact_name"
                             placeholder=""
                  />
                  <label class="col-sm-12 mb-sm-0">Contact Number</label>
                  <app-input class="col-sm-8 mr-sm-2 mb-sm-2"
                             type="tel-input-custom"
                             v-model="education.contact_number"
                             placeholder=""
                  />
                  <label class="col-sm-12 mb-sm-0">Contact Email</label>
                  <app-input class="col-sm-8 mr-sm-2 mb-sm-2"
                             type="email"
                             v-model="education.contact_email"
                             placeholder=""
                  />
                  <label class="col-sm-12 mb-sm-0">Institute Address</label>
                  <app-input class="col-sm-8 mr-sm-2 mb-sm-2"
                             type="textarea"
                             v-model="education.institute_address"
                             placeholder=""
                  />
                  <label class="col-sm-12 mb-sm-0">From</label>
                  <app-input class="col-sm-8 mr-sm-2 mb-sm-2"
                             type="date"
                             v-model="education.start_date"
                             :max-date="education.current_status ? new Date() : null"
                             placeholder=""
                  />
                  <div class="error" v-if="!$v.education.start_date.isValidFromDate">
                    From Date Can't Be Greater Than To Date.
                  </div>
                  <label class="col-sm-12 mb-sm-0">To</label>
                  <div class="d-flex align-items-center w-100">
                    <app-input
                        class="col-sm-8 mr-sm-2 mb-sm-2"
                        type="date"
                        v-model="education.end_date"
                        :disabled="education.current_status"
                        @input="refreshValidation('education')"
                    />
                    <div class="d-flex align-items-center ml-2">
                      <label class="mb-0 mr-2">To Present?</label>
                      <el-checkbox
                          v-model="education.current_status"
                          @change="checked => checked ? handleCurrentStatusChange('education') : handleUncheckCurrentStatus('education')"
                      />
                    </div>
                  </div>
                </div>
              </template>

<!--              Self Employment Form-->
              <template v-if="employment_type === historyType.SelfEmploymentTrade">
                <div class="form-group row align-items-center ml-sm-2">
                  <label class="col-sm-12 mb-sm-0">Trading Name / Business Name</label>
                  <app-input class="col-sm-8 mr-sm-2 mb-sm-2"
                             type="text"
                             v-model="selfEmployment.name"
                             placeholder=""
                  />
                  <label class="col-sm-12 mb-sm-0">UTR / Company Number</label>
                  <app-input class="col-sm-8 mr-sm-2 mb-sm-2"
                             type="text"
                             v-model="selfEmployment.utr_number"
                             placeholder=""
                  />
                  <label class="col-sm-12 mb-sm-0">Trading / Business Details</label>
                  <app-input class="col-sm-8 mr-sm-2 mb-sm-2"
                             type="textarea"
                             v-model="selfEmployment.details"
                             placeholder=""
                  />
                  <label class="col-sm-12 mb-sm-0">Trading / Business Address</label>
                  <app-input class="col-sm-8 mr-sm-2 mb-sm-2"
                             type="text"
                             v-model="selfEmployment.address"
                             placeholder=""
                  />
                  <label class="col-sm-12 mb-sm-0">Accountant Name</label>
                  <app-input class="col-sm-8 mr-sm-2 mb-sm-2"
                             type="text"
                             v-model="selfEmployment.accountant_name"
                             placeholder=""
                  />
                  <label class="col-sm-12 mb-sm-0">Accounting Firm</label>
                  <app-input class="col-sm-8 mr-sm-2 mb-sm-2"
                             type="textarea"
                             v-model="selfEmployment.accounting_firm"
                             placeholder=""
                  />
                  <label class="col-sm-12 mb-sm-0">Contact Number</label>
                  <app-input class="col-sm-8 mr-sm-2 mb-sm-2"
                             type="tel-input-custom"
                             v-model="selfEmployment.contact_number"
                             placeholder=""
                  />
                  <label class="col-sm-12 mb-sm-0">Contact Email</label>
                  <app-input class="col-sm-8 mr-sm-2 mb-sm-2"
                             type="email"
                             v-model="selfEmployment.contact_email"
                             placeholder=""
                  />
                  <label class="col-sm-12 mb-sm-0">From</label>
                  <app-input class="col-sm-8 mr-sm-2 mb-sm-2"
                             type="date"
                             v-model="selfEmployment.start_date"
                             :max-date="selfEmployment.current_status ? new Date() : null"
                             placeholder=""
                  />
                  <div class="error" v-if="!$v.selfEmployment.start_date.isValidFromDate">
                    From Date Can't Be Greater Than To Date.
                  </div>
                  <label class="col-sm-12 mb-sm-0">To</label>
                  <app-input
                      class="col-sm-8 mr-sm-2 mb-sm-2"
                      type="date"
                      v-model="selfEmployment.end_date"
                      :disabled="selfEmployment.current_status"
                      @input="refreshValidation('selfEmploymentTrade')"
                  />
                  <div class="form-group row align-items-center mt-2">
                    <label class="col-sm-8">Currently Self-Employed?</label>
                    <el-checkbox
                        v-model="selfEmployment.current_status"
                        @change="checked => checked ? handleCurrentStatusChange('selfEmploymentTrade') : handleUncheckCurrentStatus('selfEmploymentTrade')"
                    />
                  </div>
                </div>
              </template>

<!--              Unemployment Form-->
              <template v-if="employment_type === historyType.Unemployed">
                <div class="form-group row align-items-center ml-sm-2">
                  <label class="col-sm-12 mb-sm-0">Unemployment Start Date</label>
                  <app-input class="col-sm-8 mr-sm-2 mb-sm-2"
                             type="date"
                             v-model="unEmployment.start_date"
                             :max-date="unEmployment.current_status ? new Date() : null"
                             placeholder=""
                  />
                  <div class="error" v-if="!$v.unEmployment.start_date.isValidFromDate">
                    From Date Can't Be Greater Than To Date.
                  </div>

                  <label class="col-sm-12 mb-sm-0">Unemployment End Date</label>
                  <app-input
                      class="col-sm-8 mr-sm-2 mb-sm-2"
                      type="date"
                      v-model="unEmployment.end_date"
                      :disabled="unEmployment.current_status"
                      @input="refreshValidation('unemployed')"
                  />
                  <div class="form-group row align-items-center mt-2">
                    <label class="col-sm-8">Currently Unemployed?</label>
                    <el-checkbox
                        v-model="unEmployment.current_status"
                        @change="checked => checked ? handleCurrentStatusChange('unemployed') : handleUncheckCurrentStatus('unemployed')"
                    />
                  </div>
                  <label class="col-sm-12 mb-sm-0">More Information</label>
                  <app-input class="col-sm-8 mr-sm-2 mb-sm-2"
                             type="textarea"
                             v-model="unEmployment.more_information"
                             placeholder=""
                  />
                  <label class="col-sm-12 mb-sm-0">Please upload proof document for Unemployment.</label>
                  <upload-btn :fileObj="unEmployment.files"
                              :renderType="type.CandidateDetail"
                              :emailAddress="emailAddress" @selected="onCandidateDetailFileSelected('unemployed', $event)"
                              :metaData="unEmployment"
                              :auto-upload="false"
                  />
                  <div v-if="selectedFile" class="col-sm-12 document-preview-container mt-4">
                    <div class="document-preview-card" style="cursor: pointer;" @click="viewCertificate(selectedFile.file_path)">
                      <div class="file-thumbnail-box">
                        <button class="remove-preview-btn" @click.stop="removePreviewFile">
                          <app-icon name="x" style="width: 14px; height: 14px;" />
                        </button>
                        <img v-if="isImage(selectedFile.file_name)"
                             :src="getFileUrl(selectedFile.file_path)"
                             alt="Document Preview">
                        <div v-else class="file-icon-large">
                          <app-icon :name="getFileIcon(selectedFile.file_name)" style="width: 48px; height: 48px;" />
                        </div>
                      </div>
                      <div class="file-name-text">
                        {{ selectedFile.file_name }}
                      </div>
                      <div class="upload-status-badge">
                        <template v-if="selectedFile.is_pending">
                          <app-icon name="check-circle" style="width: 14px; height: 14px;" /> Selected <app-icon name="check" style="width: 14px; height: 14px;" />
                        </template>
                        <template v-else>
                          <app-icon name="check-circle" style="width: 14px; height: 14px;" /> Uploaded <app-icon name="check" style="width: 14px; height: 14px;" />
                        </template>
                      </div>
                    </div>
                  </div>
                </div>
              </template>

              <template v-if="employment_type === historyType.Others">
                <history-others
                    :othersObject="others"
                    :validator="$v.others"
                    @status-change="checked => checked ? handleCurrentStatusChange('others') : handleUncheckCurrentStatus('others')"
                />
              </template>
            </div>
          </template>

          <template slot="footer">
            <button
                type="button"
                class="btn btn-secondary mr-2"
                @click="updateModal"
                :disabled="hasValidationError"
            >
              {{ $t("Update") }}
            </button>
            <button
                type="button"
                class="btn btn-primary mr-2"
                data-dismiss="modal"
                @click="closeModal"
            >
              {{ $t("Close") }}
            </button>
          </template>
        </app-modal>
      </template>
      </slot>
    </form>
  </div>
</template>

<script>
import {ModalMixin} from "@/app/Mixins/ModalMixin";
import {required, requiredIf} from 'vuelidate/lib/validators'
import {FormMixin} from "@/core/mixins/form/FormMixin";
import HistoryPieChart from '@Views/Screening/CandidateRegistration/CandidateRegistrationStepperSteps/HistoryPieChart';
import {
  calculateDifference,
  calculateDifferenceInDays, getHighlightClass,
  getHighlightStyle, getLatestStatus,
  getStatusComment, getStatusValue, isRevertToApplicant
} from "@Views/Screening/utility/common";
import {
  _statusColors,
  employmentDetailsTypeList,
  employmentTypeList,
  p46List,
  siaLicenseTypeList,
  visaTypeList
} from "@Views/Screening/utility/constant";
import {_yesOrNoList} from "@Views/Screening/utility/constant";
import {historyType, uploadBtnRenderType} from "@Views/Screening/utility/enums";
import {File} from "@Views/Screening/utility/apiAgent";
import AppLibrary from "@/app/Helpers/AppLibrary";
import axios from "axios";
import {APPLICANT_FILES} from "@/app/Config/ScreeningAppUrl";

export default {
  name: "CandidateHistory",
  extends: AppLibrary,
  mixins: [FormMixin, ModalMixin],
  components: {
    HistoryPieChart
  },

  props: {
    isReadOnly:{
      type: Boolean,
      default: false
    },
    candidate_history_info: {
      type: Array,
    },
    dateFormatter: {
      type: Function,
    },
    isAdmin: {
      type: Boolean,
      default: false
    },
    candidateId: {
      type: Number,
    },
    emailAddress: {
      type: String
    },
    onSaveBtn: Function,
    restrictValidation: {
      type: Boolean,
      default: true,
    },
    applicant: {
      type: Object,
      default: () => ({})
    },
    company: {
      type: Object,
      default: () => ({})
    },

  },
  data() {
    return {
      siaLicenseTypeList: siaLicenseTypeList,
      licenseOptions: siaLicenseTypeList,
      visaTypeList: visaTypeList,
      employmentDetailsTypeList: employmentDetailsTypeList,
      p46List: p46List,
      employment: {},
      education: {},
      selfEmployment: {},
      unEmployment: {},
      others: {},
      employment_type: '',
      index: 0,
      resolvedStatuses: {}, // Object to store statuses by ID
      statusLinks: {}, // Object to store links by ID
      yesOrNoList: _yesOrNoList,
      modalId: "history_modal",
      is_modal: false,
      employmentTypeList: employmentTypeList,
      error: {
        isError: false,
        errorMessage: ''
      },
      historyType: historyType,
      type: uploadBtnRenderType,
      statusColors: _statusColors,
      selectedFile: null,
    };
    
  },
  methods: {
    getHighlightStyle,
    getStatusComment,
    getLatestStatus,
    getStatusValue,
    isRevertToApplicant,
    getHighlightClass,
    isImage(fileName) {
      if (!fileName) return false;
      const extension = fileName.toString().split('.').pop().toLowerCase();
      return ['jpg', 'jpeg', 'png', 'gif', 'svg'].includes(extension);
    },
    getFileIcon(fileName) {
      if (!fileName) return 'file';
      const extension = fileName.toString().split('.').pop().toLowerCase();
      if (extension === 'pdf') return 'file-text';
      return 'file';
    },
    getFileUrl(path) {
      if (!path) return '';
      const p = path.toString();
      if (p.includes('blob:') || p.includes('http')) return p;
      const normalized = p.startsWith('/') ? p : '/' + p;
      return window.location.origin + normalized;
    },
    viewCertificate(path) {
      if (!path) return;
      const url = this.getFileUrl(path);
      window.open(url, '_blank');
    },
    getFileDisplayName(val) {
      if (!val) return '';
      const s = val.toString();
      const lastSeg = s.split('/').pop().split('?')[0];
      const dotIdx = lastSeg.lastIndexOf('.');
      const name = dotIdx > 0 ? lastSeg.slice(0, dotIdx) : lastSeg;
      const ext = dotIdx > 0 ? lastSeg.slice(dotIdx + 1) : '';
      const cleaned = name.replace(/[\s_-]*\d{8,}$/, '').replace(/\s{2,}/g, ' ').replace(/[_-]{2,}/g, '_').trim();
      return ext ? `${cleaned}.${ext}` : cleaned;
    },
    onCandidateDetailFileUploaded(section, uploaded) {
      const fileEntry = {
        id: uploaded.id,
        file_name: uploaded.file_name || uploaded.name,
        path: uploaded.file_path || uploaded.path
      };
      if (section === 'unemployed') {
        if (!Array.isArray(this.unEmployment.files)) this.$set(this.unEmployment, 'files', []);
        this.unEmployment.files.push(fileEntry);
      }
      if (section === 'others') {
        if (!Array.isArray(this.others.files)) this.$set(this.others, 'files', []);
        this.others.files.push(fileEntry);
      }
    },
    getUploadedEntry(res) {
      const d = res && res.data ? res.data : null;
      if (Array.isArray(d)) {
        return d[0] || {};
      }
      const arr = d && Array.isArray(d.data) ? d.data : null;
      return (arr && arr[0]) ? arr[0] : {};
    },
    handleStatusUpdate(payload) {
      const item = this.candidate_history_info.find(
        (i) => i.id === payload.statusable_id && i.statusable_type === payload.statusable_type
      );
      if (item) {
        if (!item.screening_status) {
          this.$set(item, "screening_status", []);
        }

        // Check if this is an update to existing status or a new one
        const existingIndex = item.screening_status.findIndex(s => s.id === payload.id);
        
        if (existingIndex !== -1) {
          // Update existing status
          this.$set(item.screening_status, existingIndex, {
            ...item.screening_status[existingIndex],
            action_comment: payload.action_comment,
            created_at: payload.created_at,
            include_in_report: payload.include_in_report ?? item.screening_status[existingIndex].include_in_report,
            applicant_files: payload.applicant_files || []
          });
        } else {
          // Add new status
          item.screening_status.push({
            ...payload,
            include_in_report: !!payload.include_in_report
          });
        }

        if (payload.screening_endorsement) {
          this.$set(
            item,
            "screening_endorsement",
            payload.screening_endorsement
          );
        }
      }
    },
    handleStatusDelete(payload) {
      const item = this.candidate_history_info.find(
          (i) => i.id === payload.statusable_id && i.statusable_type === payload.type
      );
      if (item && item.screening_status) {
        item.screening_status = item.screening_status.filter(status => status.id !== payload.id);
        // Force update to ensure reactivity if needed, though reassignment should trigger it
        this.$set(item, 'screening_status', item.screening_status);

        const deletedEndorsementId = payload?.screening_endorsement?.id;
        if (deletedEndorsementId && item.screening_endorsement && item.screening_endorsement.id === deletedEndorsementId) {
          this.$set(item, 'screening_endorsement', null);
        }
      }
    },
    closeModal(){
      this.resetFormState();
      this.employment_type = '';
      this.is_modal = false;
      this.selectedFile = null;
    },
    handleUncheckCurrentStatus(type) {
      switch (type) {
        case 'employment':
          this.employment.current_status = false;
          this.employment.end_date = '';
          break;
        case 'education':
          this.education.current_status = false;
          this.education.end_date = '';
          break;
        case 'selfEmploymentTrade':
          this.selfEmployment.current_status = false;
          this.selfEmployment.end_date = '';
          break;
        case 'unemployed':
          this.unEmployment.current_status = false;
          this.unEmployment.end_date = '';
          break;
        case 'others':
          this.others.current_status = false;
          this.others.end_date = '';
          break;
      }
      this.refreshValidation(type);
    },
    handleCurrentStatusChange(type) {
      // Set selected history's current_status to true and set end_date to null to indicate "Till Present"
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const checkFutureStart = (obj) => {
        if (obj.start_date) {
          const start = new Date(obj.start_date);
          if (start > today) {
            obj.start_date = null;
          }
        }
      };

      switch (type) {
        case 'employment':
          this.employment.current_status = true;
          this.employment.end_date = new Date().toISOString().split('T')[0];
          checkFutureStart(this.employment);
          break;

        case 'education':
          this.education.current_status = true;
          this.education.end_date = new Date().toISOString().split('T')[0];
          checkFutureStart(this.education);
          break;

        case 'selfEmploymentTrade':
          this.selfEmployment.current_status = true;
          this.selfEmployment.end_date = new Date().toISOString().split('T')[0];
          checkFutureStart(this.selfEmployment);
          break;

        case 'unemployed':
          this.unEmployment.current_status = true;
          this.unEmployment.end_date = new Date().toISOString().split('T')[0];
          checkFutureStart(this.unEmployment);
          break;
        case 'others':
          this.others.current_status = true;
          this.others.end_date = new Date().toISOString().split('T')[0];
          checkFutureStart(this.others);
          break;
      }
      this.refreshValidation(type);
    },
    refreshValidation(type) {
      if (type === 'employment' && this.$v.employment) {
        this.$v.employment.start_date.$touch();
        this.$v.employment.end_date.$touch();
      }
      if (type === 'education' && this.$v.education) {
        this.$v.education.start_date.$touch();
        this.$v.education.end_date.$touch();
      }
      if (type === 'selfEmploymentTrade' && this.$v.selfEmployment) {
        this.$v.selfEmployment.start_date.$touch();
        this.$v.selfEmployment.end_date.$touch();
      }
      if (type === 'unemployed' && this.$v.unEmployment) {
        this.$v.unEmployment.start_date.$touch();
        this.$v.unEmployment.end_date.$touch();
      }
      if (type === 'others' && this.$v.others) {
        this.$v.others.start_date.$touch();
      }
    },
    textOnChange(event, model, validationType) {
      const text = event?.target.value;
      if (text) {
        if (validationType === this.validationType.AlphaNumeric16Characters) {
          this.validation[model] = this.validateAlphaNumericSixteenCharacter(text);
        }else if (validationType === this.validationType.AlphaNumeric) {
          this.validation[model] = this.validateAlphaNumeric(text);
        }else if (validationType === this.validationType.Alphabet) {
          this.validation[model] = this.validateAlphabet(text);
        }else if (validationType === this.validationType.Numeric) {
          this.validation[model] = this.validateNumeric(text);
        }else if (validationType === this.validationType.Email) {
          this.validation[model] = this.validateEmail(text);
        }
        if(!this.validation[model]){
          // this.basic_info[field] = text.slice(0, -1);
          this.$set(this.basic_info, model, text.slice(0, -1));
          // event.preventDefault();
        }
        this.$set(this.validation, model, this.validation[model]);
      }
    },
    calculateDifferenceInDays,
    calculateDifference,
    resetFormState(){
      this.employment = {};
      this.unEmployment = {};
      this.selfEmployment = {};
      this.education = {};
      this.others = {};
    },

    async addBtnHandler() {
      if (!this.isValidForm()) {
        this.$toastr.e('Please fill in all required fields correctly.');
        return;
      }

      let entry, typeKey;

      if (this.employment_type === 'employment') {
        entry = this.employment;
        typeKey = 0;
        entry.statusable_type = 'App\\Models\\Screening\\ApplicantEmployment';
        try {
          if (entry.p45_file) {
            const fd = new FormData();
            fd.append('files', entry.p45_file);
            fd.append('type', 'p45_document');
            fd.append('email', this.emailAddress);
            const res = await File.uploadDocument(fd);
            const u = this.getUploadedEntry(res);
            try {
              const existingP45 = await this.getP45Files();
              const toDelete = (existingP45 || []).map(f => f.id).filter(id => id && id !== u.id);
              if (toDelete.length) {
                await Promise.allSettled(toDelete.map(id => File.deleteDocument(id)));
              }
            } catch (e) {}
            this.$set(entry, 'p45_id', u.id);
            this.$set(entry, 'copy_of_p45', u.file_path || u.path);
            this.$set(entry, 'p45_name', u.file_name || u.name || entry.p45_name);
            this.$set(entry, 'p45_file', null);
            this.$set(entry, 'p45_preview', null);
            this.$set(entry, 'p45_pending', false);
          }
        } catch (e) {
          console.error('Uploading P45 failed', e);
        }
      } else if (this.employment_type === 'education') {
        entry = this.education;
        typeKey = 1;
        entry.statusable_type = 'App\\Models\\Screening\\ApplicantEducation';
      } else if (this.employment_type === 'selfEmploymentTrade') {
        entry = this.selfEmployment;
        typeKey = 2;
        entry.statusable_type = 'App\\Models\\Screening\\ApplicantSelfEmployment';
      } else if (this.employment_type === 'unemployed') {
        entry = this.unEmployment;
        entry.is_other = 0;
        typeKey = 3;
        entry.statusable_type = 'App\\Models\\Screening\\ApplicantUnemployment';
      } else if (this.employment_type === 'others') {
        entry = this.others;
        entry.is_other = 1;
        typeKey = 3;
        entry.statusable_type = 'App\\Models\\Screening\\ApplicantUnemployment';
      } else {
        this.$toastr.i('Select History First.');
        return;
      }

      if (!this.calculateDifferenceInDays(entry.start_date)) {
        this.$toastr.e('Difference of start date should not be greater than 28 days with previous entry.');
        return;
      }

      if (typeKey === 3) {
        try {
          const pending = Array.isArray(entry.pending_files) ? entry.pending_files : [];
          if (pending.length) {
            // Replace existing files (keep only the latest uploaded)
            const existing = Array.isArray(entry.files) ? entry.files : [];
            const existingIds = existing.map(f => f.id).filter(Boolean);
            if (existingIds.length) {
              try {
                await Promise.allSettled(existingIds.map(id => File.deleteDocument(id)));
              } catch (e) {
                console.error('Deleting previous files failed', e);
              }
            }
            const uploadedEntries = [];
            for (const pf of pending) {
              const fd = new FormData();
              fd.append('files', pf);
              fd.append('type', 'CandidateDetailDocument');
              fd.append('email', this.emailAddress);
              
              // 🔹 Pass metaData to link file specifically to this record
              if (this.unEmployment.id) {
                fd.append('metaData[statusable_type]', 'App\\Models\\Screening\\ApplicantUnemployment');
                fd.append('metaData[id]', this.unEmployment.id);
              }
              
              const res = await File.uploadDocument(fd);
              const u = (res && res.data && res.data[0]) ? res.data[0] : {};
              uploadedEntries.push({
                id: u.id,
                file_name: u.file_name || pf.name,
                path: u.file_path || u.path
              });
            }
            const first = uploadedEntries[0] ? [uploadedEntries[0]] : [];
            this.$set(entry, 'files', first);
            this.$set(entry, 'pending_files', []);
          }
        } catch (e) {
          console.error('Uploading candidate detail documents failed', e);
        }
      }

      const newEntry = { 
        ...entry, 
        type: typeKey,
        screening_status: [],
        start_date_formatted: this.dateFormatter(entry.start_date),
        end_date_formatted: this.formatEndDate(entry)
      };
      this.candidate_history_info.push(newEntry);

      try {
        this.onSaveBtn();
      } catch (e) {
        console.error(e);
      }

      // Clear the form after adding
      if (typeKey === 0) this.employment = {};
      if (typeKey === 1) this.education = {};
      if (typeKey === 2) this.selfEmployment = {};
      if (typeKey === 3) {
        this.unEmployment = {};
        this.others = {};
      }
      this.employment_type = '';
    },

    async updateBtnHandler(id, type) {
      const target = this.candidate_history_info.find(item => item.id === id && item.type === type); // Match both id and type
      if (!target) {
        console.error('No matching record found for the given ID and type');
        return; // Handle the case where no match is found
      }

      // Create a shallow copy and ensure current_status is boolean for el-checkbox
      const itemToEdit = { ...target };
      if (itemToEdit.hasOwnProperty('current_status')) {
        itemToEdit.current_status = !!itemToEdit.current_status;
      }

      if (type === 0) {
        this.employment = itemToEdit;
        if (!this.employment.is_other_employment) {
          this.$set(this.employment, 'is_other_employment', 'no');
        }
        if (!this.employment.is_have_p45_or_p46) {
          this.$set(this.employment, 'is_have_p45_or_p46', 'others');
        }
        this.employment_type = 'employment';
      }
      if (type === 1) {
        this.education = itemToEdit;
        this.employment_type = 'education';
      }
      if (type === 2) {
        this.selfEmployment = itemToEdit;
        this.employment_type = 'selfEmploymentTrade';
      }
      if (type === 3) {
        if (itemToEdit.is_other) {
          this.others = itemToEdit;
          this.employment_type = 'others';
        } else {
          this.unEmployment = itemToEdit;
          this.employment_type = 'unemployed';
        }
      }

      this.index = this.candidate_history_info.findIndex(item => item.id === id && item.type === type);

      try {
        await this.mapCandidateDetailFilesForHistory();
      } catch (e) {}

      // Set selectedFile AFTER mapping so it reflects the latest files array
      if (type === 3) {
        if (itemToEdit.is_other) {
          const latestOthers = this.candidate_history_info[this.index] || this.others;
          this.selectedFile = latestOthers.files && latestOthers.files.length ? {
            file_name: latestOthers.files[0].file_name || latestOthers.files[0].name,
            file_path: latestOthers.files[0].path || latestOthers.files[0].file_path,
            is_pending: false
          } : null;
          const othersData = { ...(this.candidate_history_info[this.index] || itemToEdit) };
          othersData.current_status = !!othersData.current_status;
          this.others = othersData;
        } else {
          const latestUnemp = this.candidate_history_info[this.index] || this.unEmployment;
          this.selectedFile = latestUnemp.files && latestUnemp.files.length ? {
            file_name: latestUnemp.files[0].file_name || latestUnemp.files[0].name,
            file_path: latestUnemp.files[0].path || latestUnemp.files[0].file_path,
            is_pending: false
          } : null;
          const unempData = { ...(this.candidate_history_info[this.index] || itemToEdit) };
          unempData.current_status = !!unempData.current_status;
          this.unEmployment = unempData;
        }
      }

      this.is_modal = true;
    },

    async removeBtnHandler(id, type) {
      const itemIndex = this.candidate_history_info.findIndex(item => item.id === id && item.type === type);
      if (itemIndex === -1) {
        console.error(`No matching record found for id: ${id} and type: ${type}`);
        return;
      }

      const target = this.candidate_history_info[itemIndex];

      if (!confirm('Are you sure you want to remove this entry? All attached comment documents will also be deleted.')) {
        return;
      }

      const fileIds = [];
      try {
        if (type === 0) {
          try {
            if (target.p45_id) {
              fileIds.push(target.p45_id);
            } else {
              const p45s = await this.getP45Files();
              const list = Array.isArray(p45s) ? p45s : [];
              if (list.length) {
                const baseName = (p) => {
                  if (!p) return '';
                  const s = p.toString();
                  const qIdx = s.indexOf('?');
                  const clean = qIdx >= 0 ? s.slice(0, qIdx) : s;
                  const parts = clean.split('/');
                  return parts[parts.length - 1] || clean;
                };
                const targetName = (target.p45_name || '').toString().toLowerCase();
                const targetBase = baseName(target.copy_of_p45).toLowerCase();
                const sDate = target.start_date ? new Date(target.start_date) : null;
                const toDate = (d) => { try { return new Date(((d || '').toString().split(' ')[0])); } catch(e) { return null; } };
                const withinDays = (a, b, days = 90) => {
                  if (!a || !b) return false;
                  return Math.abs((a - b) / (1000 * 60 * 60 * 24)) <= days;
                };
                const candidates = list.map(f => ({
                  id: f.id,
                  file_name: (f.file_name || f.name || '').toString(),
                  path: f.file_path || f.path,
                  base: baseName(f.file_path || f.path).toString(),
                  created_at: toDate(f.created_at)
                }));
                // Strong match by name/base
                const strong = candidates.filter(c =>
                  (targetName && c.file_name.toLowerCase() === targetName) ||
                  (targetBase && c.base.toLowerCase() === targetBase)
                );
                if (strong.length) {
                  strong.forEach(c => c.id && fileIds.push(c.id));
                } else if (sDate) {
                  // Fallback: closest by created_at within 90 days
                  const ranged = candidates
                    .filter(c => withinDays(c.created_at, sDate))
                    .sort((a, b) => {
                      const da = Math.abs((a.created_at - sDate) / (1000 * 60 * 60 * 24));
                      const db = Math.abs((b.created_at - sDate) / (1000 * 60 * 60 * 24));
                      if (da !== db) return da - db;
                      return (b.created_at || 0) - (a.created_at || 0);
                    });
                  if (ranged[0] && ranged[0].id) fileIds.push(ranged[0].id);
                }
              }
            }
          } catch (e) {}
        }
        if (Array.isArray(target.screening_status)) {
          target.screening_status.forEach(st => {
            const files = st && st.applicant_files;
            if (Array.isArray(files)) {
              files.forEach(f => {
                let fid = null;
                if (typeof f === 'number') fid = f;
                else if (typeof f === 'string') fid = Number(f);
                else fid = f?.id || f?.file_id || f?.document_id;
                if (fid) fileIds.push(fid);
              });
            }
          });
        }
        if (Array.isArray(target.files)) {
          target.files.forEach(f => {
            let fid = null;
            if (typeof f === 'number') fid = f;
            else if (typeof f === 'string') fid = Number(f);
            else fid = f?.id || f?.file_id || f?.document_id;
            if (fid) fileIds.push(fid);
          });
        }
      } catch (e) {
        console.error('Collecting file ids failed', e);
      }

      if (fileIds.length) {
        try {
          await Promise.allSettled(
            [...new Set(fileIds)].map(fid => File.deleteDocument(fid))
          );
        } catch (e) {
          console.error('Deleting attached documents failed', e);
        }
      }

      this.candidate_history_info.splice(itemIndex, 1);
      this.$toastr.s('History removed and linked documents cleared. Click on apply changes to take effect.');
      try {
        if (typeof this.onSaveBtn === 'function') {
          this.onSaveBtn();
        }
      } catch (e) {
        console.error('Error calling onSaveBtn after remove:', e);
      }
    },


    documentp45Change(payload) {
      const file = (payload && payload.target && payload.target.files)
        ? payload.target.files[0]
        : (payload && payload.name ? payload : null);
      if (file) {
        this.$set(this.employment, 'p45_file', file);
        this.$set(this.employment, 'p45_name', file.name);
        this.$set(this.employment, 'p45_pending', true);
        this.$set(this.employment, 'copy_of_p45', null);
        if (file.type && file.type.startsWith('image/')) {
          this.$set(this.employment, 'p45_preview', URL.createObjectURL(file));
        } else {
          this.$set(this.employment, 'p45_preview', null);
        }
      }
    },
    async removeEmploymentP45() {
      try {
        if (!confirm('Are you sure you want to delete this document?')) return;
        if (this.employment.p45_pending && this.employment.p45_file) {
          this.$set(this.employment, 'p45_file', null);
          this.$set(this.employment, 'p45_name', null);
          this.$set(this.employment, 'p45_preview', null);
          this.$set(this.employment, 'p45_pending', false);
          if (typeof this.index === 'number' && this.index >= 0) {
            const orig = this.candidate_history_info[this.index] || {};
            const updated = { ...orig, p45_id: null, copy_of_p45: null, p45_name: null };
            this.$set(this.candidate_history_info, this.index, updated);
          }
          try { if (typeof this.onSaveBtn === 'function') this.onSaveBtn(); } catch(e) {}
          this.$hub.$emit("candidateDetailActiveIndex", 4);
          return;
        }
        if (this.employment.p45_id) {
          try { await File.deleteDocument(this.employment.p45_id); } catch (e) {}
        } else if (this.employment.copy_of_p45) {
          try {
            const list = await this.getP45Files();
            const match = (list || []).find(f => (f.file_path || f.path) === this.employment.copy_of_p45);
            if (match && match.id) { await File.deleteDocument(match.id); }
          } catch (e) {}
        }
      } finally {
        this.$set(this.employment, 'p45_id', null);
        this.$set(this.employment, 'copy_of_p45', null);
        this.$set(this.employment, 'p45_name', null);
        this.$set(this.employment, 'p45_preview', null);
        this.$set(this.employment, 'p45_pending', false);
        if (typeof this.index === 'number' && this.index >= 0) {
          const orig = this.candidate_history_info[this.index] || {};
          const updated = { ...orig, p45_id: null, copy_of_p45: null, p45_name: null };
          this.$set(this.candidate_history_info, this.index, updated);
        }
        try { if (typeof this.onSaveBtn === 'function') this.onSaveBtn(); } catch(e) {}
        this.$hub.$emit("candidateDetailActiveIndex", 4);
      }
    },
    async removeEmploymentP45ForItem(item) {
      try {
        if (!confirm('Are you sure you want to delete this document?')) return;
        if (item.p45_id) {
          try { await File.deleteDocument(item.p45_id); } catch (e) {}
        } else if (item.copy_of_p45) {
          try {
            const list = await this.getP45Files();
            const match = (list || []).find(f => (f.file_path || f.path) === item.copy_of_p45);
            if (match && match.id) { await File.deleteDocument(match.id); }
          } catch (e) {}
        }
      } finally {
        this.$set(item, 'p45_id', null);
        this.$set(item, 'copy_of_p45', null);
        this.$set(item, 'p45_name', null);
        this.$hub.$emit("candidateDetailActiveIndex", 4);
      }
    },
    async removeUnemploymentFile(file, index) {
       try {
         if (!confirm('Are you sure you want to delete this document?')) return;
         let fid = file?.id || file?.file_id || file?.document_id || null;
         if (!fid) {
           try {
             const list = await this.getCandidateDetailFiles();
             const match = (list || []).find(f => (f.file_path || f.path) === (file.path || file.file_path));
             if (match && match.id) fid = match.id;
           } catch (e) {}
         }
         if (fid) {
           try { await File.deleteDocument(fid); } catch (e) {}
         }
       } finally {
         try {
           if (Array.isArray(this.unEmployment.files)) {
             this.unEmployment.files.splice(index, 1);
           }
         } catch (e) {}
         this.$hub.$emit("candidateDetailActiveIndex", 4);
       }
     },
     async removePreviewFile() {
       if (!this.selectedFile) return;
       if (this.selectedFile.is_pending) {
         // Just clear pending files if it's not saved yet
         if (this.employment_type === 'unemployed') {
           this.$set(this.unEmployment, 'pending_files', []);
         } else if (this.employment_type === 'others') {
           this.$set(this.others, 'pending_files', []);
         }
         this.selectedFile = this.unEmployment.files && this.unEmployment.files.length ? {
           file_name: this.unEmployment.files[0].file_name || this.unEmployment.files[0].name,
           file_path: this.unEmployment.files[0].path || this.unEmployment.files[0].file_path,
           is_pending: false
         } : null;
       } else {
         // Saved file, so delete it from DB
         if (this.employment_type === 'unemployed') {
           await this.removeUnemploymentFile(this.unEmployment.files[0], 0);
         }
         this.selectedFile = null;
       }
     },
    //todo
    //duplicate function, need refactoring
    uploadBtnHandler(_file, _type, _issueDate = null, _expiryDate = null) {
      let data = {email: this.emailAddress, type: _type, files: _file};
      if (_issueDate){
        data.issue_date = _issueDate
      }
      if (_expiryDate){
        data._expiryDate = _issueDate
      }
      let formData = new FormData;
      for (const [key, value] of Object.entries(data)) {
        formData.append(key, value);
      }
      File.uploadDocument(formData)
          .then((response) => {
            this.employment.copy_of_p45 = response.data[0]?.file_path
            this.$toastr.s(response.data.message);
          })
          .catch(({error}) => {
            //trigger after error
          })
          .finally(() => {
            this.$hub.$emit("reload-" + this.tableId);
          });
    },
    async fetchStatus(screening_endorsement_id) {
      try {
        // If link already exists, open it directly
        if (this.statusLinks[screening_endorsement_id]) {
          window.open(this.statusLinks[screening_endorsement_id], "_blank");
          return;
        }

        // Fetch the link if not available
        const response = await fetch(`/screening-endorsement/link/${screening_endorsement_id}`);
        let data = await response.text(); // Get response as text

        // Update the status and link for the specific ID
        this.$set(this.resolvedStatuses, screening_endorsement_id, data);
        this.$set(this.statusLinks, screening_endorsement_id, data);

        // Open the link after fetching
        window.open(data, "_blank");
      } catch (error) {
        console.error("Error fetching status:", error);
        this.$set(this.resolvedStatuses, screening_endorsement_id, "Error fetching status");
      }
    }
    ,
    async updateModal() {
      if (this.index === -1) {
        this.$toastr.e('Could not find the record to update.');
        return;
      }

      if (this.employment_type === 'employment') {
        try {
          if (this.employment.p45_file) {
            // Upload the new P45 first
            const fd = new FormData();
            fd.append('files', this.employment.p45_file);
            fd.append('type', 'p45_document');
            fd.append('email', this.emailAddress);
            const res = await File.uploadDocument(fd);
            const u = this.getUploadedEntry(res);
            if (!u || !u.id) {
              this.$toastr.e('P45 upload failed. Please try again.');
              return;
            }
            // After successful upload, delete ALL other P45 so only new one remains
            try {
              const existingP45 = await this.getP45Files();
              const toDelete = (existingP45 || []).map(f => f.id).filter(id => id && id !== u.id);
              if (toDelete.length) {
                await Promise.allSettled(toDelete.map(id => File.deleteDocument(id)));
              }
            } catch (e) {}
            this.$set(this.employment, 'p45_id', u.id);
            this.$set(this.employment, 'copy_of_p45', u.file_path || u.path);
            this.$set(this.employment, 'p45_name', u.file_name || u.name || this.employment.p45_name);
            this.$set(this.employment, 'p45_file', null);
            this.$set(this.employment, 'p45_preview', null);
            this.$set(this.employment, 'p45_pending', false);
          } else {
            // No new file selected; if server has no P45 then clear UI state
            try {
              const list = await this.getP45Files();
              if (!list || list.length === 0) {
                this.$set(this.employment, 'p45_id', null);
                this.$set(this.employment, 'copy_of_p45', null);
                this.$set(this.employment, 'p45_name', null);
                this.$set(this.employment, 'p45_preview', null);
                this.$set(this.employment, 'p45_pending', false);
              }
            } catch (e) {}
          }
        } catch (e) {
          console.error('Uploading P45 failed', e);
        }
        const original = this.candidate_history_info[this.index] || {};
        const safeUpdate = {
          ...original,
          ...this.employment,
          id: original.id,
          type: original.type ?? 0,
          screening_status: Array.isArray(original.screening_status) ? original.screening_status : [],
          start_date_formatted: this.dateFormatter(this.employment.start_date),
          end_date_formatted: this.formatEndDate(this.employment)
        };
        this.$set(this.candidate_history_info, this.index, safeUpdate);
      }
      if (this.employment_type === 'education') {
        const original = this.candidate_history_info[this.index] || {};
        const safeUpdate = {
          ...original,
          ...this.education,
          id: original.id,
          type: original.type ?? 1,
          screening_status: Array.isArray(original.screening_status) ? original.screening_status : [],
          start_date_formatted: this.dateFormatter(this.education.start_date),
          end_date_formatted: this.formatEndDate(this.education)
        };
        this.$set(this.candidate_history_info, this.index, safeUpdate);
      }
      if (this.employment_type === 'selfEmploymentTrade') {
        const original = this.candidate_history_info[this.index] || {};
        const safeUpdate = {
          ...original,
          ...this.selfEmployment,
          id: original.id,
          type: original.type ?? 2,
          screening_status: Array.isArray(original.screening_status) ? original.screening_status : [],
          start_date_formatted: this.dateFormatter(this.selfEmployment.start_date),
          end_date_formatted: this.formatEndDate(this.selfEmployment)
        };
        this.$set(this.candidate_history_info, this.index, safeUpdate);
      }
      if (this.employment_type === 'unemployed') {
        try {
          const pending = Array.isArray(this.unEmployment.pending_files) ? this.unEmployment.pending_files : [];
          if (pending.length) {
            // Replace existing files for this entry
            const existing = Array.isArray(this.unEmployment.files) ? this.unEmployment.files : [];
            const existingIds = existing.map(f => f.id).filter(Boolean);
            if (existingIds.length) {
              try {
                await Promise.allSettled(existingIds.map(id => File.deleteDocument(id)));
              } catch (e) {
                console.error('Deleting previous files failed', e);
              }
            }
            const uploadedEntries = [];
            for (const pf of pending) {
              const fd = new FormData();
              fd.append('files', pf);
              fd.append('type', 'CandidateDetailDocument');
              fd.append('email', this.emailAddress);
              
              // 🔹 Pass metaData to link file specifically to this record
              if (this.others.id) {
                fd.append('metaData[statusable_type]', 'App\\Models\\Screening\\ApplicantUnemployment');
                fd.append('metaData[id]', this.others.id);
              }
              
              const res = await File.uploadDocument(fd);
              const u = this.getUploadedEntry(res);
              uploadedEntries.push({
                id: u.id,
                file_name: u.file_name || pf.name,
                path: u.file_path || u.path
              });
            }
            const first = uploadedEntries[0] ? [uploadedEntries[0]] : [];
            this.$set(this.unEmployment, 'files', first);
            this.$set(this.unEmployment, 'pending_files', []);
          }
        } catch (e) {
          console.error('Uploading candidate detail documents failed', e);
        }
        const original = this.candidate_history_info[this.index] || {};
        const safeUpdate = {
          ...original,
          ...this.unEmployment,
          id: original.id,
          type: original.type ?? 3,
          is_other: 0,
          screening_status: Array.isArray(original.screening_status) ? original.screening_status : [],
          start_date_formatted: this.dateFormatter(this.unEmployment.start_date),
          end_date_formatted: this.formatEndDate(this.unEmployment)
        };
        this.$set(this.candidate_history_info, this.index, safeUpdate);
      }
      if (this.employment_type === 'others') {
        try {
          const pending = Array.isArray(this.others.pending_files) ? this.others.pending_files : [];
          if (pending.length) {
            // Replace existing files for this entry
            const existing = Array.isArray(this.others.files) ? this.others.files : [];
            const existingIds = existing.map(f => f.id).filter(Boolean);
            if (existingIds.length) {
              try {
                await Promise.allSettled(existingIds.map(id => File.deleteDocument(id)));
              } catch (e) {
                console.error('Deleting previous files failed', e);
              }
            }
            const uploadedEntries = [];
            for (const pf of pending) {
              const fd = new FormData();
              fd.append('files', pf);
              fd.append('type', 'CandidateDetailDocument');
              fd.append('email', this.emailAddress);
              const res = await File.uploadDocument(fd);
              const u = this.getUploadedEntry(res);
              uploadedEntries.push({
                id: u.id,
                file_name: u.file_name || pf.name,
                path: u.file_path || u.path
              });
            }
            const first = uploadedEntries[0] ? [uploadedEntries[0]] : [];
            this.$set(this.others, 'files', first);
            this.$set(this.others, 'pending_files', []);
          }
        } catch (e) {
          console.error('Uploading candidate detail documents failed', e);
        }
        const original = this.candidate_history_info[this.index] || {};
        const safeUpdate = {
          ...original,
          ...this.others,
          id: original.id,
          type: original.type ?? 3,
          is_other: 1,
          screening_status: Array.isArray(original.screening_status) ? original.screening_status : [],
          start_date_formatted: this.dateFormatter(this.others.start_date),
          end_date_formatted: this.formatEndDate(this.others)
        };
        this.$set(this.candidate_history_info, this.index, safeUpdate);
      }
      // Persist changes by calling parent save handler if provided
      try {
        if (typeof this.onSaveBtn === 'function') {
          this.onSaveBtn();
        }
      } catch (e) {
        console.error('Error calling onSaveBtn after update:', e);
      }

      this.resetFormState();
      this.employment_type = '';
      $(`#${this.modalId}`).modal('hide');
    },
    onCandidateDetailFileSelected(section, file) {
      const previewFile = {
        file_name: file.name,
        file_path: URL.createObjectURL(file),
        is_pending: true
      };
      this.selectedFile = previewFile;

      if (section === 'unemployed') {
        this.$set(this.unEmployment, 'pending_files', [file]);
      } else if (section === 'others') {
        this.$set(this.others, 'pending_files', [file]);
      }
    },
    async getCandidateDetailFiles() {
      try {
        const res = await axios.get(`${APPLICANT_FILES}?email=${this.emailAddress}`);
        const files = res.data.data || res.data || [];
        return files.filter(f => (f.type || '').toString().toLowerCase() === 'candidatedetaildocument');
      } catch (e) {
        console.error('Fetching candidate detail files failed', e);
        return [];
      }
    },
    async getP45Files() {
      try {
        const res = await axios.get(`${APPLICANT_FILES}?email=${this.emailAddress}`);
        const files = res.data.data || res.data || [];
        return files.filter(f => (f.type || '').toString().toLowerCase() === 'p45_document');
      } catch (e) {
        console.error('Fetching P45 files failed', e);
        return [];
      }
    },
    async mapCandidateDetailFilesForHistory() {
      try {
        if (!Array.isArray(this.candidate_history_info) || this.candidate_history_info.length === 0) return;
        const cdFiles = await this.getCandidateDetailFiles();
        const usedIds = new Set();
        const normalizeDate = (d) => (d || '').toString().split(' ')[0];
        const toDate = (d) => {
          if (!d) return null;
          try { return new Date(d); } catch(e) { return null; }
        };
        const dayDiff = (a, b) => {
          if (!a || !b) return Number.POSITIVE_INFINITY;
          return Math.abs((a - b) / (1000 * 60 * 60 * 24));
        };
        const filesNormalized = cdFiles.map(f => ({
          id: f.id,
          file_name: f.file_name || f.name,
          path: f.file_path || f.path,
          created_at: normalizeDate(f.created_at),
          created_at_date: toDate(normalizeDate(f.created_at))
        }));
        // Sort entries by start_date to assign deterministically
        const entryIndexes = this.candidate_history_info
          .map((item, idx) => ({ item, idx }))
          .filter(({ item }) => item && item.type === 3)
          .sort((a, b) => {
            const ad = toDate(a.item.start_date);
            const bd = toDate(b.item.start_date);
            if (!ad && !bd) return 0;
            if (!ad) return 1;
            if (!bd) return -1;
            return ad - bd;
          });
        for (const { item, idx } of entryIndexes) {
          // Respect already assigned file ids
          if (Array.isArray(item.files) && item.files.length && item.files[0]?.id) {
            usedIds.add(item.files[0].id);
            continue;
          }
          const sDateStr = (item.start_date || '').toString();
          const eDateStr = (item.end_date || '').toString();
          const sDate = toDate(sDateStr);
          const eDate = toDate(eDateStr);
          const withinRange = (file) => {
            if (!sDate) return false;
            const cd = file.created_at_date;
            if (!cd) return false;
            if (!eDate) return cd.getTime() === sDate.getTime();
            return cd >= sDate && cd <= eDate;
          };
          // Prefer within range, otherwise closest by day difference
          let candidates = filesNormalized.filter(f => !usedIds.has(f.id));
          let ranged = candidates.filter(f => withinRange(f));
          let chosen = null;
          if (ranged.length) {
            ranged.sort((a, b) => {
              // prefer closest to start_date, newer first as tiebreaker
              const da = dayDiff(a.created_at_date, sDate);
              const db = dayDiff(b.created_at_date, sDate);
              if (da !== db) return da - db;
              return (b.created_at_date || 0) - (a.created_at_date || 0);
            });
            chosen = ranged[0];
          } else {
            // fallback: closest by date
            candidates.sort((a, b) => {
              const da = dayDiff(a.created_at_date, sDate);
              const db = dayDiff(b.created_at_date, sDate);
              if (da !== db) return da - db;
              return (b.created_at_date || 0) - (a.created_at_date || 0);
            });
            chosen = candidates[0];
          }
          if (chosen) {
            this.$set(this.candidate_history_info[idx], 'files', [{ id: chosen.id, file_name: chosen.file_name, path: chosen.path }]);
            usedIds.add(chosen.id);
          }
        }
      } catch (e) {
        console.error('Mapping candidate detail files failed', e);
      }
    },
    formatEndDate(item) {
      if (!item) return 'N/A';
      if (item.current_status) return 'Till Present';
      return item.end_date ? this.dateFormatter(item.end_date) : 'N/A';
    },
    getEffectiveEndDate(entry) {
      // For sorting and calculations treat current (null) end_date as today
      return entry && entry.end_date ? new Date(entry.end_date) : new Date();
    },
    getValueById(id = null) {
      if(!id) return;
      // Find the object with the id "selfEmploymentTrade"
      const result = this.employmentTypeList.find(
        (item) => item.id === id
      );
      // Set the value property
      return result.value;
    },
    },

  validations: {
    employment: {
      is_other_employment: {
        required: requiredIf(function () {
          return this.is_modal;
        })
      },
      is_have_p45_or_p46: { required },
      start_date: {
        required,
        isValidFromDate(value) {
          if (this.employment.current_status) return true;
          if (!this.employment.end_date || !value) return true;
          
          const start = new Date(value);
          const end = new Date(this.employment.end_date);
          
          return start <= end;
        }
      },
      end_date: {
        required: requiredIf(function() {
          return !this.employment.current_status;
        })
      },
    },
    education: {
      start_date: {
        required,
        isValidFromDate(value) {
          if (this.education.current_status) return true;
          if (!this.education.end_date || !value) return true;
          
          const start = new Date(value);
          const end = new Date(this.education.end_date);
          
          return start <= end;
        }
      },
      end_date: {
        required: requiredIf(function() {
          return !this.education.current_status;
        })
      },
    },
    selfEmployment: {
      start_date: {
        required,
        isValidFromDate(value) {
          if (this.selfEmployment.current_status) return true;
          if (!this.selfEmployment.end_date || !value) return true;
          
          const start = new Date(value);
          const end = new Date(this.selfEmployment.end_date);
          
          return start <= end;
        }
      },
      end_date: {
        required: requiredIf(function() {
          return !this.selfEmployment.current_status;
        })
      },
    },
    others: {
      start_date: {
        required,
        isValidFromDate(value) {
          if (this.others.current_status) return true;
          if (!this.others.end_date) return true;
          const start = new Date(value);
          const end = new Date(this.others.end_date);
          if (value && this.others.end_date && (start <= end)) {
            return true;
          }
          return false;
        }
      },
      end_date: {}
    },
    unEmployment: {
      start_date: {
        required,
        isValidFromDate(value) {
          if (this.unEmployment.current_status) return true;
          if (!this.unEmployment.end_date || !value) return true;
          
          const start = new Date(value);
          const end = new Date(this.unEmployment.end_date);
          
          return start <= end;
        }
      },
      end_date: {},
    }
  },
  mounted() {
    this.getValueById(); // Call the method when the component is mounted
    // this.fetchStatus()
    this.mapCandidateDetailFilesForHistory();
  },
  created(){
    this.$hub.$emit("candidateDetailActiveIndex", 5);

  },
  watch: {
    // Removed automatic mapping on every change to prevent file leaking between cards.
    // Mapping is now handled explicitly during initialization and upload.
  },
  computed: {
    hasValidationError() {
      // Check validation only for the active form type
      if (this.employment_type === 'employment') {
        return this.$v.employment && this.$v.employment.$invalid;
      }
      if (this.employment_type === 'education') {
        return this.$v.education && this.$v.education.$invalid;
      }
      if (this.employment_type === 'selfEmploymentTrade') {
        return this.$v.selfEmployment && this.$v.selfEmployment.$invalid;
      }
      if (this.employment_type === 'unemployed') {
        return this.$v.unEmployment && this.$v.unEmployment.$invalid;
      }
      if (this.employment_type === 'others') {
        return this.$v.others && this.$v.others.$invalid;
      }
      return false;
    },
    hasScreeningSchedule() {
      const s = this.candidate_history_info?.screeningSchedule;
      if (!s) return false;
      if (Array.isArray(s)) return s.length > 0;
      if (typeof s === 'object') return Object.keys(s).length > 0;
      return !!s;
    },
    pieChartData() {
      // Use screening creation date as the reference anchor, fallback to today
      let referenceDate = new Date();
      const screeningSchedule = this.candidate_history_info?.screeningSchedule;
      if (screeningSchedule && screeningSchedule.created_at) {
        try {
          referenceDate = new Date(screeningSchedule.created_at);
        } catch (e) {
          // fallback to today
        }
      }

      const today = referenceDate;
      const fiveYearsAgo = new Date(referenceDate);
      fiveYearsAgo.setFullYear(fiveYearsAgo.getFullYear() - 5);
      
      // Calculate actual total days in the 5-year window
      const totalDays = Math.round((today - fiveYearsAgo) / (1000 * 60 * 60 * 24));
      
      // Calculate filled days only within the 5-year window
      const filledDays = this.sortedCandidateHistory.reduce((total, entry) => {
        const entryStart = new Date(entry.start_date);
        // Clamp end date: current entries or entries ending after reference → use reference date
        let rawEnd = this.getEffectiveEndDate(entry);
        if (rawEnd > today) rawEnd = today;
        const entryEnd = rawEnd;
        
        // Clamp entry dates to the 5-year window
        const effectiveStart = entryStart < fiveYearsAgo ? fiveYearsAgo : entryStart;
        const effectiveEnd = entryEnd > today ? today : entryEnd;
        
        // Only count if entry overlaps with 5-year window
        if (effectiveEnd > fiveYearsAgo && effectiveStart < today) {
          const diffDays = Math.round((effectiveEnd - effectiveStart) / (1000 * 60 * 60 * 24));
          return total + (diffDays > 0 ? diffDays : 0);
        }
        
        return total;
      }, 0);

      const missingDays = totalDays - filledDays;

      return {
        labels: ['Filled History', 'Missing History'],
        datasets: [
          {
            backgroundColor: ['#42b983', '#ff4d4f'],
            data: [filledDays, missingDays > 0 ? missingDays : 0]
          }
        ]
      };
    },
    latestStatus() {
      // Get the latest screening status
      return this.getLatestScreeningStatus(item.screening_status);
    },
    latestStatusColorAndValue() {
      // Get the color and value for the latest status
      return this.getStatusColorAndValue(this.latestStatus?.status);
    },
    sortedCandidateHistory(){
      return [...this.candidate_history_info].sort((a, b) => this.getEffectiveEndDate(b) - this.getEffectiveEndDate(a));
    },

    historyGaps() {
      let gaps = [];
      
      // Use screening creation date if available, otherwise use today's date
      let referenceDate = new Date();
      const screeningSchedule = this.candidate_history_info?.screeningSchedule;
      
      if (screeningSchedule && screeningSchedule.created_at) {
        try {
          // Parse the created_at (screening creation date)
          referenceDate = new Date(screeningSchedule.created_at);
        } catch (e) {
          // Failed to parse, will use today's date
        }
      }
      
      const startOfDay = (d) => {
        const x = new Date(d);
        x.setHours(0, 0, 0, 0);
        return x;
      };

      const today = startOfDay(referenceDate);
      const fiveYearsAgo = startOfDay(new Date(today));
      fiveYearsAgo.setFullYear(fiveYearsAgo.getFullYear() - 5);
      
      const DAY_MS = 1000 * 60 * 60 * 24;

      // Duration breakdown (same intent as yours, but operates on Date objects)
      function getDurationBreakdown(start, end) {
        let startDate = new Date(start);
        let endDate = new Date(end);

        let years = endDate.getFullYear() - startDate.getFullYear();
        let months = endDate.getMonth() - startDate.getMonth();
        let days = endDate.getDate() - startDate.getDate();

        if (days < 0) {
          months--;
          days += new Date(endDate.getFullYear(), endDate.getMonth(), 0).getDate();
        }
        if (months < 0) {
          years--;
          months += 12;
        }

        const parts = [];
        if (years > 0) parts.push(`${years} year${years > 1 ? "s" : ""}`);
        if (months > 0) parts.push(`${months} month${months > 1 ? "s" : ""}`);
        if (days > 0 || parts.length === 0) parts.push(`${days} day${days > 1 ? "s" : ""}`);

        return parts.join(", ");
      }

      // -----------------------------
      // Window + dates
      // -----------------------------

      const gracePeriodEnd = startOfDay(new Date(today));
      gracePeriodEnd.setDate(gracePeriodEnd.getDate() - 40);

      // -----------------------------
      // Normalize + sort
      // -----------------------------
      const sortedHistory = [...(this.candidate_history_info || [])]
          .map((h) => {
            // If current_status means "ongoing", treat end as reference date (creation date).
            // Also clamp any end_date that exceeds the reference date back to the reference date.
            let effectiveEnd;
            if (h.current_status) {
              effectiveEnd = today;
            } else if (h.end_date) {
              const parsedEnd = startOfDay(new Date(h.end_date));
              effectiveEnd = parsedEnd > today ? today : parsedEnd;
            } else {
              effectiveEnd = today;
            }

            return {
              ...h,
              effectiveStartDate: startOfDay(new Date(h.start_date)),
              effectiveEndDate: effectiveEnd,
            };
          })
          // sort by start date oldest first
          .sort((a, b) => a.effectiveStartDate - b.effectiveStartDate);

      // If no history, whole 5-year window is "missing"
      if (sortedHistory.length === 0) {
        const daysDiff = Math.round((today - fiveYearsAgo) / DAY_MS);
        if (daysDiff > 0) {
          gaps.push({
            start: fiveYearsAgo,
            end: today,
            days: daysDiff,
            duration: getDurationBreakdown(fiveYearsAgo, today),
          });
        }
        return gaps;
      }

      // -----------------------------
      // 1) Gap from 5 years ago to first entry (clamped)
      // -----------------------------
      const firstStart = sortedHistory[0].effectiveStartDate;

      if (firstStart > fiveYearsAgo) {
        const start = fiveYearsAgo;
        const end = sortedHistory.length > 0 ? new Date(sortedHistory[0].start_date) : today;
        const daysDiff = Math.round((end - start) / (1000 * 60 * 60 * 24));
        
        // Only show gap if there's more than 1 day difference
        if (daysDiff > 1) {
          gaps.push({
            start,
            end,
            days: daysDiff,
            duration: getDurationBreakdown(start, end),
          });
        }
      }

      // -----------------------------
      // 2) Gaps BETWEEN entries (merge overlaps)
      // -----------------------------
      let coverageEnd = sortedHistory[0].effectiveEndDate;

      for (let i = 0; i < sortedHistory.length - 1; i++) {
        const next = sortedHistory[i + 1];
        const nextStart = next.effectiveStartDate;
        const nextEnd = next.effectiveEndDate;

        // If our coverage hasn't reached the 5-year window yet, jump forward
        if (coverageEnd < fiveYearsAgo) {
          coverageEnd = nextEnd;
          continue;
        }

        // Clamp the start point of comparison to window start
        const compareEnd = coverageEnd < fiveYearsAgo ? fiveYearsAgo : coverageEnd;

        const daysDiff = Math.round((nextStart - compareEnd) / (1000 * 60 * 60 * 24));
        
        // Only show gap if there's more than 1 day difference (0 or 1 day is continuous)
        if (daysDiff > 1) {
          const gapStart = compareEnd < fiveYearsAgo ? fiveYearsAgo : compareEnd;
          const gapEnd = nextStart;

          gaps.push({
            start: gapStart,
            end: gapEnd,
            days: Math.round((gapEnd - gapStart) / DAY_MS),
            duration: getDurationBreakdown(gapStart, gapEnd),
          });
        }

        // Extend coverage if next ends later (this is the key overlap fix)
        if (nextEnd > coverageEnd) {
          coverageEnd = nextEnd;
        }
      }

      // 🔥 Check trailing gap from last entry to today (if not "Currently Here")
      if (sortedHistory.length > 0) {
        // Find the entry with the latest end date (or current entry)
        let latestEntry = sortedHistory[0];
        let latestEndDate = latestEntry.effectiveEndDate;
        
        for (let entry of sortedHistory) {
          if (entry.effectiveEndDate > latestEndDate) {
            latestEntry = entry;
            latestEndDate = entry.effectiveEndDate;
          }
        }
        
        // Only show gap if:
        // 1. Latest entry is NOT current (has end_date AND current_status is false/undefined)
        // 2. AND the end date is before today
        const isCurrentEntry = latestEntry.current_status === true || 
                              latestEntry.current_status === 1 || 
                              latestEntry.current_status === '1' ||
                              latestEntry.current_status === 'true';
        
        if (!isCurrentEntry && latestEntry.end_date && latestEndDate < today) {
          const daysDiff = Math.round((today - latestEndDate) / (1000 * 60 * 60 * 24));
          
          // Only show gap if there's more than 1 day difference
          if (daysDiff > 1) {
            gaps.push({
              start: latestEndDate,
              end: today,
              days: daysDiff,
              duration: getDurationBreakdown(latestEndDate, today),
            });
          }
        }
      }

      // Optional: Filter out any gaps fully before the 5-year window
      return gaps.filter(g => g.end > fiveYearsAgo);
    }



  },
  watch:{

  },

}
</script>

<style scoped>
/* make card relative so the badge can be positioned */
.card-item{ position: relative; overflow: visible; padding-top: 30px; }

.current-address {
  position: absolute;
  content: '';
  top: 0px;
  right: 15px;
  left: auto;
  width: auto;
  min-width: 110px;
  background: #2b303b;
  color: #ffffff !important;
  padding: 5px 10px;
  border-radius: 0 5px 0 0;
  text-align: center;
}

/* For printed/downloaded documents, keep the badge in the flow to avoid overlap */
@media print {
  .current-address {
    position: static !important;
    display: inline-block !important;
    margin-bottom: 6px !important;
    background: #2b303b !important;
    color: #ffffff !important;
    border-radius: 0 5px 0 0 !important;
    padding: 5px 10px !important;
    text-align: center !important;
  }
  .card-item { padding-top: 0 !important; }
}

/* Ensure action buttons sit inline and align under inputs */
.card-item .action-buttons {
  display: flex !important;
  flex-wrap: nowrap !important;
  align-items: center !important;
  gap: 8px;
  padding-left: 0;
  margin-left: 0;
}

/* Ensure children that try to be block don't force a wrap */
.card-item .action-buttons > * {
  display: inline-flex !important;
  align-items: center !important;
  margin-right: 0 !important;
}

.card-item .action-buttons .screening-note {
  display: inline-flex;
}

/* Slightly move Update button to the right */
.card-item .action-buttons .btn-update {
  margin-left: 27px !important;
}

/* Document preview styles (shared with Training) */
.document-preview-container {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 10px;
  justify-content: flex-start;
  padding-left: 15px;
  padding-right: 15px;
}
.document-preview-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: auto;
  animation: fadeIn 0.4s ease;
}
.file-thumbnail-box {
  width: 140px;
  height: 100px;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
}
.file-thumbnail-box:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
}
.file-thumbnail-box img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 5px;
}
.file-icon-large {
  color: #6c757d;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.file-name-text {
  font-weight: 600;
  margin-top: 5px;
  font-size: 14px;
  color: #3498db;
  max-width: 100%;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.2s;
}
.document-preview-card:hover .file-name-text {
  color: #2980b9;
  text-decoration: underline;
}
.upload-status-badge {
  color: #27ae60;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  font-weight: 600;
}
/* cross remove button on preview */
.remove-preview-btn {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 50%;
  background: #ffffff;
  color: #ff4d4f;
  box-shadow: 0 1px 4px rgba(0,0,0,0.15);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2;
}
.remove-preview-btn:hover {
  background: #ffebeb;
}
@keyframes fadeIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}

</style>
